import { readCachedUser } from "../utils/cachedUser";
import BackButton from "../components/BackButton";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  doc, getDoc, updateDoc, arrayUnion, collection,
  addDoc, onSnapshot, orderBy, query, serverTimestamp, setDoc,
  getDocs, where, writeBatch,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, storage, functions } from "../firebase";
import { calcFee, calcDiscountedFee, validateCoupon } from "../services/wallet";
import { notif } from "../services/notifications";
import NotificationBell from "../components/NotificationBell";
import { perfMark } from "../utils/perf";
import Icon from "../components/Icon";
import { stripEmoji } from "../utils/text";
import useDialogAccessibility from "../hooks/useDialogAccessibility";

const css = `
*{box-sizing:border-box;margin:0;padding:0;}
  .cp{font-family:'Prompt',sans-serif;background:var(--bg);color:var(--text);height:100vh;display:flex;flex-direction:column;overflow:hidden;}
  .cp-top{display:flex;align-items:center;gap:10px;padding:10px 16px;background:var(--surface);border-bottom:1px solid var(--border);flex-shrink:0;}
  .cp-back{width:30px;height:30px;border-radius:50%;border:1px solid var(--border);background:transparent;color:var(--text-muted);cursor:pointer;font-size:1rem;display:flex;align-items:center;justify-content:center;transition:all 0.2s;flex-shrink:0;}
  .cp-back:hover{border-color:var(--accent);color:var(--accent);}
  .cp-info{flex:1;min-width:0;}
  .cp-id{font-size:0.6rem;color:var(--text-muted);}
  .cp-name{font-size:0.88rem;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .cp-price{font-size:0.7rem;color:var(--accent);}
  .cp-sbadge{padding:3px 10px;border-radius:var(--radius-full);font-size:0.65rem;font-weight:600;flex-shrink:0;}
  .cp-sbadge.created{background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.25);color:var(--warning);}
  .cp-sbadge.deposited{background:rgba(85,153,255,0.1);border:1px solid rgba(85,153,255,0.25);color:var(--info);}
  .cp-sbadge.shipped{background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.25);color:var(--accent);}
  .cp-sbadge.confirmed{background:rgba(247,71,53,0.15);border:1px solid rgba(247,71,53,0.3);color:var(--accent);}
  .cp-sbadge.released{background:rgba(247,71,53,0.2);border:1px solid rgba(247,71,53,0.4);color:var(--accent);}
  .cp-sbadge.completed{background:rgba(247,71,53,0.15);border:1px solid rgba(247,71,53,0.3);color:var(--accent);}
  .cp-sbadge.disputed{background:rgba(255,85,85,0.1);border:1px solid rgba(255,85,85,0.25);color:var(--danger);}
  .cp-codebar{display:flex;align-items:center;justify-content:space-between;padding:4px 16px;background:var(--surface2);border-bottom:1px solid var(--border);flex-shrink:0;}
  .cp-codelabel{font-size:0.62rem;color:var(--text-muted);}
  .cp-codeval{font-family:'DM Mono',monospace;color:var(--accent);font-weight:700;letter-spacing:2px;margin:0 6px;}
  .cp-copybtn{padding:2px 8px;border-radius:var(--radius-full);border:1px solid rgba(247,71,53,0.2);background:transparent;color:var(--accent);font-size:0.62rem;cursor:pointer;font-family:'Prompt',sans-serif;}
  .cp-prog{padding:6px 16px;background:var(--surface2);border-bottom:1px solid var(--border);flex-shrink:0;}
  .cp-steps{display:flex;align-items:center;max-width:600px;margin:0 auto;}
  .cp-step{display:flex;align-items:center;flex:1;gap:3px;position:relative;}
  .cp-step:not(:last-child)::after{content:'';position:absolute;left:20px;right:-4px;top:50%;height:1px;background:var(--border);z-index:0;}
  .cp-step.done:not(:last-child)::after{background:var(--accent);opacity:0.4;}
  .cp-circle{width:20px;height:20px;border-radius:50%;border:1.5px solid var(--border);display:flex;align-items:center;justify-content:center;font-size:0.52rem;background:var(--bg);position:relative;z-index:1;flex-shrink:0;color:var(--text-muted);}
  .cp-step.done .cp-circle{border-color:var(--accent);background:rgba(247,71,53,0.1);color:var(--accent);}
  .cp-step.active .cp-circle{border-color:var(--warning);background:rgba(255,179,71,0.1);color:var(--warning);}
  .cp-slabel{font-size:0.48rem;color:var(--text-muted);white-space:nowrap;}
  .cp-step.done .cp-slabel{color:var(--accent);}
  .cp-step.active .cp-slabel{color:var(--warning);}
  .cp-body{display:flex;flex:1;overflow:hidden;}
  .cp-panel{width:256px;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow-y:auto;flex-shrink:0;}
  .cp-psec{padding:12px;border-bottom:1px solid var(--border);}
  .cp-ptitle{font-size:0.63rem;letter-spacing:2px;text-transform:uppercase;color:var(--text-muted);margin-bottom:10px;}
  .cp-irow{display:flex;justify-content:space-between;margin-bottom:7px;}
  .cp-rl{font-size:0.72rem;color:var(--text-muted);}
  .cp-rv{font-size:0.78rem;font-weight:500;}
  .cp-rv.g{color:var(--accent);}
  .cp-rv.w{color:var(--warning);}
  .cp-rv.b{color:var(--info);}
  .cp-member{display:flex;align-items:center;gap:8px;padding:8px 10px;background:var(--surface2);border-radius:10px;border:1px solid var(--border);margin-bottom:6px;}
  .cp-mav{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.75rem;font-weight:700;flex-shrink:0;}
  .cp-mname{font-size:0.78rem;font-weight:600;}
  .cp-mrole{font-size:0.63rem;color:var(--text-muted);}
  .cp-waiting{font-size:0.72rem;color:var(--text-muted);padding:8px;background:rgba(255,179,71,0.05);border:1px dashed rgba(255,179,71,0.2);border-radius:8px;text-align:center;margin-bottom:6px;}
  .cp-escrow-sec{padding:12px;border-bottom:1px solid var(--border);}
  .cp-escrow-title{font-size:0.63rem;letter-spacing:2px;text-transform:uppercase;color:var(--text-muted);margin-bottom:10px;}
  .cp-abtn{width:100%;padding:11px 14px;border-radius:10px;border:none;font-family:'Prompt',sans-serif;font-size:0.85rem;font-weight:700;cursor:pointer;transition:all 0.2s;margin-bottom:8px;display:flex;align-items:center;justify-content:center;gap:8px;}
  .cp-abtn:last-child{margin-bottom:0;}
  .cp-abtn:disabled{opacity:0.5;cursor:not-allowed;}
  .cp-abtn.pay{background:var(--info);color:#fff;}
  .cp-abtn.pay:hover:not(:disabled){background:#4488ee;transform:translateY(-1px);}
  .cp-abtn.ship{background:var(--warning);color:#f5f6f8;}
  .cp-abtn.ship:hover:not(:disabled){background:#e0a030;transform:translateY(-1px);}
  .cp-abtn.receive{background:var(--accent);color:#f5f6f8;}
  .cp-abtn.receive:hover:not(:disabled){background:var(--accent2);transform:translateY(-1px);}
  .cp-abtn.claim{background:transparent;border:1px solid rgba(255,85,85,0.4);color:var(--danger);}
  .cp-abtn.claim:hover:not(:disabled){background:rgba(255,85,85,0.08);}
  .cp-abtn.dispute{background:transparent;border:1px solid rgba(255,85,85,0.3);color:var(--danger);}
  .cp-abtn.dispute:hover:not(:disabled){background:rgba(255,85,85,0.08);}
  .cp-abtn.done{background:rgba(247,71,53,0.08);border:1px solid rgba(247,71,53,0.2);color:var(--accent);cursor:default;}
  .cp-wait-box{padding:10px 12px;border-radius:10px;background:var(--surface2);border:1px solid var(--border);font-size:0.78rem;color:var(--text-muted);text-align:center;margin-bottom:8px;}
  .cp-mobile-actions{display:none;}
  .cp-chat{flex:1;display:flex;flex-direction:column;overflow:hidden;}
  .cp-msgs{flex:1;overflow-y:auto;padding:16px 20px;display:flex;flex-direction:column;gap:12px;}
  .cp-sys{align-self:center;font-size:0.7rem;color:var(--text-muted);padding:5px 14px;background:rgba(255,255,255,0.03);border-radius:var(--radius-full);border:1px solid var(--border);text-align:center;max-width:92%;}
  .cp-mrow{display:flex;gap:8px;align-items:flex-end;}
  .cp-mrow.right{flex-direction:row-reverse;}
  .cp-av{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.75rem;flex-shrink:0;}
  .cp-bubble{max-width:65%;min-width:120px;padding:10px 14px;border-radius:16px;font-size:0.88rem;line-height:1.65;word-break:normal;overflow-wrap:anywhere;}
  .cp-bubble.buyer{background:rgba(42,90,154,0.25);border:1px solid rgba(42,90,154,0.35);border-bottom-right-radius:4px;}
  .cp-bubble.seller{background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.2);border-bottom-left-radius:4px;}
  .cp-bubble.ai{background:var(--surface2);border:1px solid var(--border);border-bottom-left-radius:4px;font-size:0.82rem;}
  .cp-bubble.admin{background:rgba(255,179,71,0.08);border:1px solid rgba(255,179,71,0.25);border-bottom-left-radius:4px;}
  .cp-bname{font-size:0.65rem;color:var(--text-muted);margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px;}
  .cp-btime{font-size:0.6rem;color:var(--text-muted);margin-top:3px;display:block;}
  .cp-typing{display:flex;gap:4px;align-items:center;padding:10px 14px;}
  .cp-td{width:5px;height:5px;background:var(--text-muted);border-radius:50%;}
  .cp-td:nth-child(1){animation:td 1.2s infinite;}
  .cp-td:nth-child(2){animation:td 1.2s 0.2s infinite;}
  .cp-td:nth-child(3){animation:td 1.2s 0.4s infinite;}
  @keyframes td{0%,60%,100%{opacity:0.3;transform:translateY(0)}30%{opacity:1;transform:translateY(-4px)}}
  .cp-inputbar{padding:12px 16px;background:var(--surface);border-top:1px solid var(--border);flex-shrink:0;}
  .cp-quick{display:flex;gap:6px;margin-bottom:8px;flex-wrap:wrap;}
  .cp-qbtn{padding:3px 10px;border-radius:var(--radius-full);border:1px solid var(--border);background:transparent;color:var(--text-muted);font-size:0.7rem;cursor:pointer;font-family:'Prompt',sans-serif;white-space:nowrap;transition:all 0.2s;}
  .cp-qbtn:hover{border-color:var(--accent);color:var(--accent);}
  .cp-inputrow{display:flex;align-items:flex-end;gap:8px;}
  .cp-inputwrap{flex:1;background:var(--surface2);border:1px solid var(--border);border-radius:12px;display:flex;align-items:flex-end;padding:8px 12px;transition:border-color 0.2s;}
  .cp-inputwrap:focus-within{border-color:rgba(247,71,53,0.3);}
  .cp-textarea{flex:1;background:transparent;border:none;outline:none;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.85rem;resize:none;max-height:100px;min-height:22px;line-height:1.5;}
  .cp-textarea::placeholder{color:var(--text-muted);}
  .cp-send{width:40px;height:40px;border-radius:10px;border:none;background:var(--accent);color:#f5f6f8;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0;transition:all 0.2s;}
  .cp-send:hover{background:var(--accent2);}
  .cp-send:disabled{opacity:0.5;cursor:not-allowed;}
  .cp-modal{position:fixed;inset:0;background:rgba(0,0,0,0.75);backdrop-filter:blur(8px);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
  .cp-mbox{background:var(--surface);border:1px solid var(--border);border-radius:18px;padding:28px;width:100%;max-width:420px;max-height:90vh;overflow-y:auto;}
  .cp-mtitle{font-size:1.05rem;font-weight:700;margin-bottom:8px;}
  .cp-mdesc{font-size:0.82rem;color:var(--text-muted);margin-bottom:20px;line-height:1.6;}
  .cp-mbtns{display:flex;gap:10px;}
  .cp-mbtns button{flex:1;padding:11px;border-radius:10px;cursor:pointer;font-family:'Prompt',sans-serif;font-size:0.85rem;font-weight:600;}
  .cp-mcancel{background:transparent;border:1px solid var(--border);color:var(--text-muted);}
  .cp-mok{background:var(--accent);border:none;color:#f5f6f8;}
  .cp-mok.pay{background:var(--info);}
  .cp-mok.receive{background:var(--accent);}
  .cp-mdanger{background:var(--danger);border:none;color:#fff;}
  .cp-mta{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.83rem;resize:none;outline:none;margin-bottom:14px;}
  .cp-minput{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:10px 12px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.85rem;outline:none;margin-bottom:10px;}
  .cp-minput:focus{border-color:rgba(247,71,53,0.4);}
  .cp-merr{background:rgba(255,85,85,0.1);border:1px solid rgba(255,85,85,0.3);border-radius:8px;padding:8px 12px;color:var(--danger);font-size:0.78rem;margin-bottom:12px;}
  .cp-msummary{background:var(--surface2);border-radius:10px;padding:12px;margin-bottom:16px;}
  .cp-msrow{display:flex;justify-content:space-between;font-size:0.8rem;margin-bottom:6px;}
  .cp-msrow:last-child{margin-bottom:0;border-top:1px solid var(--border);padding-top:6px;font-weight:700;}
  .cp-msrow span:last-child{color:var(--accent);}
  .cp-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;gap:12px;color:var(--text-muted);font-family:'Prompt',sans-serif;}
  .cp-spinner{width:28px;height:28px;border:2px solid var(--border);border-top-color:var(--accent);border-radius:50%;animation:spin 0.8s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg)}}
  .toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--surface);border:1px solid rgba(247,71,53,0.3);border-radius:10px;padding:10px 20px;font-size:0.82rem;color:var(--accent);z-index:300;}
  /* Photo upload */
  .cp-photo-group{margin-bottom:12px;}
  .cp-photo-label{font-size:0.72rem;color:var(--text-muted);margin-bottom:6px;display:block;}
  .cp-photo-input{width:100%;padding:8px 12px;background:var(--surface2);border:1px dashed rgba(247,71,53,0.3);border-radius:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.8rem;cursor:pointer;}
  .cp-photo-input::-webkit-file-upload-button{background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.3);color:var(--accent);padding:4px 10px;border-radius:6px;cursor:pointer;font-family:'Prompt',sans-serif;font-size:0.75rem;margin-right:8px;}
  .cp-photo-prev{width:100%;height:90px;object-fit:cover;border-radius:8px;margin-top:6px;border:1px solid var(--border);display:block;}
  .cp-photo-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:8px;margin-top:8px;}
  .cp-file-item{position:relative;}
  .cp-file-remove{position:absolute;top:4px;right:4px;width:22px;height:22px;border-radius:50%;border:none;background:rgba(10,15,13,0.85);color:#fff;cursor:pointer;font-size:0.75rem;line-height:1;display:flex;align-items:center;justify-content:center;}
  .cp-file-add-hint{font-size:0.68rem;color:var(--text-muted);margin-top:6px;}
  .cp-ship-photos{display:flex;flex-direction:column;gap:4px;}
  /* Terms modal */
  .cp-terms-box{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:14px;max-height:220px;overflow-y:auto;font-size:0.78rem;color:var(--text-muted);line-height:1.7;margin-bottom:16px;}
  .cp-terms-box h4{color:var(--text);font-size:0.85rem;margin-bottom:8px;}
  .cp-terms-box p{margin-bottom:8px;}
  .cp-terms-check{display:flex;align-items:center;gap:10px;margin-bottom:16px;cursor:pointer;font-size:0.82rem;}
  .cp-terms-check input{width:16px;height:16px;accent-color:var(--accent);}
  /* Role select */
  .cp-role-btns{display:flex;gap:10px;margin-bottom:16px;}
  .cp-role-btn{flex:1;padding:12px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.85rem;cursor:pointer;transition:all 0.2s;text-align:center;}
  .cp-role-btn.active{border-color:var(--accent);background:rgba(247,71,53,0.08);color:var(--accent);font-weight:600;}
  /* Ship photos */
  .cp-ship-photos{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;}
  .cp-auto-release-banner{background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.3);border-radius:10px;padding:10px 14px;font-size:0.78rem;color:var(--warning);margin-bottom:8px;text-align:center;}
  /* Balance box */
  .cp-balance-box{background:rgba(247,71,53,0.05);border:1px solid rgba(247,71,53,0.2);border-radius:10px;padding:12px 14px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:center;}
  .cp-balance-label{font-size:0.75rem;color:var(--text-muted);}
  .cp-balance-val{font-size:1.1rem;font-weight:700;color:var(--accent);}
  .cp-balance-low{border-color:rgba(255,85,85,0.3)!important;background:rgba(255,85,85,0.05)!important;}
  .cp-balance-low .cp-balance-val{color:var(--danger)!important;}
  /* Fee tier table */
  .cp-fee-tiers{background:var(--surface2);border-radius:10px;padding:10px 12px;margin-bottom:14px;}
  .cp-fee-tiers-title{font-size:0.65rem;letter-spacing:1px;text-transform:uppercase;color:var(--text-muted);margin-bottom:8px;}
  .cp-fee-row{display:flex;justify-content:space-between;font-size:0.74rem;padding:4px 0;border-bottom:1px solid var(--border);}
  .cp-fee-row:last-child{border-bottom:none;}
  .cp-fee-row.active-tier{color:var(--accent);font-weight:700;}
  /* Deadline banner */
  .cp-deadline-banner{background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.3);border-radius:10px;padding:10px 14px;font-size:0.8rem;color:var(--warning);margin-bottom:14px;display:flex;align-items:center;gap:8px;}
  /* Courier select */
  .cp-mselect{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:10px 12px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.85rem;outline:none;margin-bottom:10px;appearance:none;cursor:pointer;}
  .cp-mselect:focus{border-color:rgba(247,71,53,0.4);}
  /* Ship preview */
  .cp-ship-preview{background:var(--surface2);border-radius:10px;padding:12px;margin-bottom:14px;}
  .cp-ship-preview-title{font-size:0.65rem;letter-spacing:1px;text-transform:uppercase;color:var(--text-muted);margin-bottom:8px;}
  /* Checklist */
  .cp-checklist{background:var(--surface2);border-radius:10px;padding:12px;margin-bottom:14px;}
  .cp-check-item{display:flex;align-items:flex-start;gap:10px;padding:8px 0;border-bottom:1px solid var(--border);cursor:pointer;font-size:0.83rem;user-select:none;}
  .cp-check-item:last-child{border-bottom:none;padding-bottom:0;}
  .cp-check-box{width:20px;height:20px;border-radius:5px;border:2px solid var(--border);display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:0.72rem;transition:all 0.2s;margin-top:1px;}
  .cp-check-box.checked{border-color:var(--accent);background:rgba(247,71,53,0.15);color:var(--accent);}
  /* Tracking info */
  .cp-tracking-card{background:rgba(85,153,255,0.08);border:1px solid rgba(85,153,255,0.2);border-radius:10px;padding:12px 14px;margin-bottom:14px;}
  .cp-tracking-row{display:flex;justify-content:space-between;font-size:0.8rem;margin-bottom:5px;}
  .cp-tracking-row:last-child{margin-bottom:0;}
  .cp-tracking-row span:last-child{color:var(--info);font-weight:600;}
  /* Seller proof photos */
  .cp-seller-photos{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px;}
  .cp-seller-photo{width:100%;height:90px;object-fit:cover;border-radius:8px;border:1px solid var(--border);}
  .cp-seller-photo-ph{width:100%;height:90px;border-radius:8px;border:1px dashed var(--border);display:flex;align-items:center;justify-content:center;font-size:0.72rem;color:var(--text-muted);}
  /* Upload progress */
  .cp-upload-progress{margin-top:8px;}
  .cp-upload-bar-wrap{background:rgba(255,255,255,0.06);border-radius:100px;height:6px;overflow:hidden;margin-bottom:3px;}
  .cp-upload-bar{height:6px;border-radius:100px;background:linear-gradient(90deg,var(--accent),#e63626);transition:width 0.3s ease;}
  .cp-upload-pct{font-size:0.7rem;color:var(--text-muted);display:flex;justify-content:space-between;}
  .cp-file-size{font-size:0.68rem;color:var(--text-muted);margin-top:3px;}
  /* Top-up link */
  .cp-topup-link{display:inline-flex;align-items:center;gap:5px;font-size:0.78rem;color:var(--accent);background:rgba(247,71,53,0.08);border:1px solid rgba(247,71,53,0.2);border-radius:6px;padding:5px 12px;cursor:pointer;font-family:'Prompt',sans-serif;margin-top:6px;}
  /* Coupon */
  .cp-coupon-wrap{margin-bottom:14px;}
  .cp-coupon-label{font-size:0.72rem;color:var(--text-muted);margin-bottom:6px;display:flex;align-items:center;gap:5px;}
  .cp-coupon-row{display:flex;gap:8px;}
  .cp-coupon-input{flex:1;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:9px 12px;color:var(--text);font-family:'DM Mono',monospace;font-size:0.88rem;outline:none;text-transform:uppercase;letter-spacing:1px;transition:border-color 0.2s;}
  .cp-coupon-input:focus{border-color:rgba(247,71,53,0.4);}
  .cp-coupon-input::placeholder{color:var(--text-muted);font-family:'Prompt',sans-serif;text-transform:none;letter-spacing:0;}
  .cp-coupon-btn{padding:9px 14px;border-radius:10px;border:1px solid rgba(247,71,53,0.3);background:transparent;color:var(--accent);font-family:'Prompt',sans-serif;font-size:0.82rem;font-weight:600;cursor:pointer;white-space:nowrap;transition:all 0.2s;}
  .cp-coupon-btn:hover{background:rgba(247,71,53,0.08);}
  .cp-coupon-btn:disabled{opacity:0.5;cursor:not-allowed;}
  .cp-coupon-ok{background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.3);border-radius:10px;padding:10px 12px;margin-top:8px;display:flex;align-items:center;justify-content:space-between;font-size:0.8rem;}
  .cp-coupon-ok-left{display:flex;align-items:center;gap:7px;color:var(--accent);}
  .cp-coupon-ok-right{font-size:0.78rem;color:var(--text-muted);cursor:pointer;background:none;border:none;font-family:'Prompt',sans-serif;}
  .cp-coupon-ok-right:hover{color:var(--danger);}
  .cp-coupon-err{font-size:0.76rem;color:var(--danger);margin-top:6px;}
  .cp-discount-row{color:var(--accent);font-size:0.8rem;}
  .cp-orig-fee{text-decoration:line-through;color:var(--text-muted);font-size:0.78rem;margin-right:4px;}
  /* Shipping card */
  .cp-dfield{margin-bottom:14px;}
  .cp-dlabel{display:flex;align-items:center;gap:6px;font-size:0.78rem;font-weight:600;color:var(--text);margin-bottom:6px;}
  .cp-dlabel svg{color:var(--warning);}
  .cp-dwrap{position:relative;}
  .cp-dinput{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:12px;padding:12px 14px;color:var(--text);font-family:'DM Mono',monospace;font-size:0.88rem;outline:none;transition:border-color 0.2s,box-shadow 0.2s;}
  .cp-dinput::placeholder{color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.82rem;}
  .cp-dinput:focus{border-color:rgba(255,179,71,0.65);box-shadow:0 0 0 3px rgba(255,179,71,0.12);}
  .cp-dinput.has-btn{padding-right:46px;}
  .cp-dtext{font-family:'Prompt',sans-serif;font-size:0.85rem;line-height:1.55;resize:vertical;min-height:84px;}
  .cp-deye{position:absolute;right:6px;top:50%;transform:translateY(-50%);width:34px;height:34px;display:flex;align-items:center;justify-content:center;background:transparent;border:none;border-radius:8px;color:var(--text-muted);cursor:pointer;}
  .cp-deye:hover{color:var(--warning);background:rgba(255,179,71,0.08);}
  .cp-dhint{display:flex;gap:8px;align-items:flex-start;font-size:0.72rem;line-height:1.5;color:var(--text-muted);margin:2px 0 16px;}
  .cp-dhint svg{margin-top:2px;color:var(--text-muted);}
  .cp-dc-row{padding:9px 0;border-top:1px solid rgba(255,179,71,0.15);}
  .cp-dc-row:first-of-type{border-top:none;padding-top:2px;}
  .cp-dc-label{display:flex;align-items:center;gap:6px;font-size:0.68rem;color:var(--text-muted);margin-bottom:4px;}
  .cp-dc-line{display:flex;align-items:center;gap:8px;}
  .cp-dc-val{flex:1;min-width:0;font-family:'DM Mono',monospace;font-size:0.9rem;color:var(--text);font-weight:600;word-break:break-all;}
  .cp-dc-val.note{font-family:'Prompt',sans-serif;font-weight:400;font-size:0.82rem;line-height:1.55;white-space:pre-wrap;}
  .cp-dc-btn{flex-shrink:0;width:30px;height:30px;display:flex;align-items:center;justify-content:center;background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.25);border-radius:8px;color:#c77b27;cursor:pointer;}
  .cp-dc-btn:hover{background:rgba(255,179,71,0.2);}
  .cp-dc-hint{margin-top:10px;font-size:0.68rem;color:var(--text-muted);line-height:1.5;}
  .cp-ship-card{background:rgba(255,179,71,0.06);border:1px solid rgba(255,179,71,0.25);border-radius:14px;padding:14px;max-width:100%;}
  .cp-ship-card-title{font-size:0.72rem;letter-spacing:1.5px;text-transform:uppercase;color:#c77b27;margin-bottom:10px;display:flex;align-items:center;gap:6px;}
  .cp-ship-card-row{display:flex;justify-content:space-between;align-items:center;font-size:0.8rem;margin-bottom:7px;gap:10px;}
  .cp-ship-card-label{color:var(--text-muted);flex-shrink:0;}
  .cp-ship-card-val{color:var(--text);font-weight:600;text-align:right;word-break:break-all;}
  .cp-ship-card-val.tracking{font-family:'DM Mono',monospace;color:#c77b27;font-size:0.85rem;}
  .cp-ship-card-media{margin-top:10px;display:flex;flex-direction:column;gap:8px;}
  .cp-ship-card-media video{width:100%;border-radius:8px;border:1px solid rgba(255,179,71,0.2);background:#000;max-height:160px;}
  .cp-ship-card-media img{width:100%;border-radius:8px;border:1px solid rgba(255,179,71,0.2);object-fit:cover;max-height:140px;display:block;cursor:pointer;}
  .cp-ship-card-media-label{font-size:0.65rem;color:var(--text-muted);margin-bottom:3px;}
  /* Mobile panel overlay */
  .cp-panel-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:150;backdrop-filter:blur(4px);}
  .cp-panel-overlay.open{display:block;}
  .cp-panel-drawer{position:fixed;bottom:0;left:0;right:0;max-height:80vh;overflow-y:auto;background:var(--surface);border-top:1px solid var(--border);border-radius:20px 20px 0 0;z-index:151;padding-bottom:env(safe-area-inset-bottom,0);}
  .cp-panel-handle{width:40px;height:4px;background:rgba(255,255,255,0.15);border-radius:2px;margin:12px auto 0;}
  .cp-info-toggle{width:32px;height:32px;border-radius:8px;border:1px solid var(--border);background:transparent;color:var(--text-muted);cursor:pointer;font-size:0.85rem;display:none;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.2s;}
  .cp-info-toggle:hover{border-color:var(--accent);color:var(--accent);}
  @media(max-width:768px){
    .cp-panel{display:none;}
    .cp-info-toggle{display:flex;}
    .cp-top{padding:10px 14px;}
    .cp-name{font-size:0.85rem;}
    .cp-codebar{padding:5px 12px;}
    .cp-prog{padding:6px 12px;}
    .cp-slabel{font-size:0.44rem;}
    .cp-msgs{padding:10px 12px;}
    .cp-quick{display:none;}
    .cp-inputbar{padding:10px 12px;}
    .cp-mbox{padding:20px;margin:0;border-radius:20px 20px 0 0;position:fixed;bottom:0;left:0;right:0;max-height:90vh;overflow-y:auto;}
    .cp-modal{align-items:flex-end;padding:0;}
    .cp-bubble{max-width:78%;}
    .cp-mobile-actions{display:flex;flex-direction:column;gap:6px;padding:10px 12px;background:var(--surface);border-top:1px solid var(--border);flex-shrink:0;}
    .cp-mobile-actions .cp-abtn{margin-bottom:0;padding:12px 14px;font-size:0.88rem;}
    .cp-mobile-actions .cp-wait-box{margin-bottom:0;font-size:0.8rem;}
  }
  @media(max-width:480px){
    .cp-price{font-size:0.62rem;}
    .cp-codeval{font-size:0.72rem;letter-spacing:1px;}
    .cp-codelabel{font-size:0.58rem;}
    .cp-copybtn{font-size:0.58rem;padding:2px 6px;}
    .cp-circle{width:18px;height:18px;font-size:0.48rem;}
    .cp-slabel{display:none;}
    .cp-msgs{padding:8px 10px;}
    .cp-bubble{max-width:82%;font-size:0.85rem;}
    .cp-mbox{padding:16px;}
    .cp-mbtns{flex-direction:column;}
    .cp-role-btns{gap:6px;}
  }
  @media(max-width:360px){
    .cp-inputbar{padding:8px;}
    .cp-send{width:36px;height:36px;font-size:0.9rem;}
    .cp-top{gap:6px;}
    .cp-name{font-size:0.78rem;}
    .cp-bubble{max-width:88%;}
    .cp-star{font-size:1.8rem;}
    .cp-img-preview-bar{flex-wrap:wrap;gap:6px;}
    .cp-img-preview-name{display:none;}
    .cp-img-btn{width:32px;height:32px;font-size:0.95rem;}
  }
  /* Partner rating badge */
  .cp-rating-badge{display:inline-flex;align-items:center;gap:4px;background:rgba(255,204,0,0.08);border:1px solid rgba(255,204,0,0.2);border-radius:6px;padding:2px 8px;font-size:0.72rem;color:#ffcc00;margin-top:3px;}
  .cp-rating-badge.no-review{color:var(--text-muted);background:transparent;border-color:var(--border);}
  /* Review modal */
  .cp-star-row{display:flex;gap:6px;justify-content:center;margin:10px 0 6px;}
  .cp-star{font-size:2.2rem;cursor:pointer;color:rgba(255,255,255,0.15);transition:color 0.15s,transform 0.1s;line-height:1;user-select:none;}
  .cp-star.filled{color:#ffcc00;}
  .cp-star:hover{transform:scale(1.2);color:#ffcc00;}
  .cp-review-done{background:rgba(255,204,0,0.07);border:1px solid rgba(255,204,0,0.25);border-radius:10px;padding:8px 12px;font-size:0.75rem;color:#ffcc00;text-align:center;margin-top:4px;}
  /* Chat image */
  .cp-img-btn{width:36px;height:36px;border-radius:9px;border:1px solid var(--border);background:transparent;color:var(--text-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1.05rem;flex-shrink:0;transition:all 0.2s;}
  .cp-img-btn:hover{border-color:var(--accent);color:var(--accent);}
  .cp-img-btn:disabled{opacity:0.4;cursor:not-allowed;}
  .cp-chat-img{max-width:220px;max-height:220px;border-radius:10px;border:1px solid var(--border);object-fit:cover;cursor:pointer;display:block;}
  .cp-img-preview-bar{display:flex;align-items:center;gap:8px;padding:6px 0 2px;border-top:1px solid var(--border);margin-top:6px;}
  .cp-img-preview-thumb{width:48px;height:48px;object-fit:cover;border-radius:7px;border:1px solid rgba(247,71,53,0.35);}
  .cp-img-preview-name{font-size:0.72rem;color:var(--text-muted);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .cp-img-cancel{background:none;border:none;color:var(--danger);cursor:pointer;font-size:1rem;padding:0 2px;line-height:1;}
  .cp-img-send{padding:5px 12px;border-radius:7px;border:none;background:var(--accent);color:#f5f6f8;font-family:'Prompt',sans-serif;font-size:0.78rem;font-weight:700;cursor:pointer;white-space:nowrap;}
  .cp-img-send:disabled{opacity:0.5;cursor:not-allowed;}
`;

const STEPS = ["สร้างห้อง", "วางเงิน", "ส่งมอบ", "ยืนยันรับ", "เสร็จสิ้น"];
const STATUS_STEP = { created: 1, deposited: 2, shipped: 3, confirmed: 4, released: 5, completed: 5, refunded: 5, disputed: 3 };
const STATUS_LABEL = {
  created: "รอชำระเงิน",
  deposited: "รอจัดส่ง",
  shipped: "รอยืนยันรับสินค้า",
  confirmed: "ยืนยันรับของแล้ว",
  released: "โอนเงินแล้ว",
  completed: "เสร็จสิ้น",
  disputed: "ข้อพิพาท",
};

const TERMS_KEY = (roomId) => `klang_terms_${roomId}`;
const MAX_SHIP_FILES = 10; // จำกัดจำนวนไฟล์วีดีโอ/รูปต่อครั้ง กันอัปโหลดเยอะเกินจำเป็น

// ── Escrow Cloud Functions — ล็อก/ปล่อย/คืนเงินจริงทั้งหมดรันฝั่ง server ────
// (แทนที่ lockMoney/releaseMoney/refundMoney เดิมใน services/wallet.js)
const escrowLockFn    = httpsCallable(functions, "escrowLock");
const escrowReleaseFn = httpsCallable(functions, "escrowRelease");
const escrowRefundFn  = httpsCallable(functions, "escrowRefund");

// การ์ดแสดงข้อมูลที่ผู้ขายส่งมอบ (ไอดี / รหัสผ่าน / อื่นๆ) — มีปุ่มคัดลอกแต่ละช่อง และซ่อนรหัสผ่านไว้จนกดดู
function DeliveryCard({ data }) {
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState("");
  const copy = (key, value) => {
    try { navigator.clipboard.writeText(value); } catch { /* ไม่รองรับ clipboard */ }
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };
  const rows = [
    { key: "id", label: "ไอดี", icon: "user", value: data.id },
    { key: "password", label: "รหัสผ่าน", icon: "lock", value: data.password, secret: true },
    { key: "note", label: "อื่นๆ", icon: "info", value: data.note, note: true },
  ].filter((r) => r.value);
  return (
    <div className="cp-ship-card">
      <div className="cp-ship-card-title">ข้อมูลที่ส่งมอบ</div>
      {rows.map((r) => (
        <div key={r.key} className="cp-dc-row">
          <div className="cp-dc-label"><Icon name={r.icon} size={12} /> {r.label}</div>
          <div className="cp-dc-line">
            <div className={`cp-dc-val${r.note ? " note" : ""}`}>
              {r.secret && !reveal ? "••••••••••" : r.value}
            </div>
            {r.secret && (
              <button type="button" className="cp-dc-btn" onClick={() => setReveal((v) => !v)} title={reveal ? "ซ่อน" : "แสดง"}>
                <Icon name={reveal ? "eye-off" : "eye"} size={15} />
              </button>
            )}
            {!r.note && (
              <button type="button" className="cp-dc-btn" onClick={() => copy(r.key, r.value)} title="คัดลอก">
                <Icon name={copied === r.key ? "check" : "copy"} size={15} />
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="cp-dc-hint">ตรวจสอบว่าเข้าใช้งานได้จริงก่อนกดยืนยันรับสินค้า</div>
    </div>
  );
}

export default function ChatPage() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get("admin") === "true";
  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [chatHeight, setChatHeight] = useState(() => window.visualViewport?.height || window.innerHeight);
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () => { if (!viewport || viewport.scale === 1) setChatHeight(viewport?.height || window.innerHeight); };
    viewport?.addEventListener("resize", resize);
    window.addEventListener("resize", resize);
    resize();
    return () => { viewport?.removeEventListener("resize", resize); window.removeEventListener("resize", resize); };
  }, []);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [modal, setModal] = useState(null);
  const [dispute, setDispute] = useState("");
  const [tracking, setTracking] = useState("");
  const [courier, setCourier] = useState("");
  // สินค้าดิจิทัล — ข้อมูลที่ผู้ขายส่งมอบ แยก 3 ช่อง: ไอดี / รหัสผ่าน / อื่นๆ
  const [deliveryId, setDeliveryId]       = useState("");
  const [deliveryPw, setDeliveryPw]       = useState("");
  const [deliveryNote, setDeliveryNote]   = useState("");
  const [showDeliveryPw, setShowDeliveryPw] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [modalErr, setModalErr] = useState("");

  // Terms & role selection for new joiners
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [joinRole, setJoinRole] = useState("buyer");
  const [pendingJoin, setPendingJoin] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [checkItems, setCheckItems] = useState([false, false, false]);

  const [showPanelDrawer, setShowPanelDrawer] = useState(false);
  const panelDrawerRef = useRef(null);
  const dialogRef = useRef(null);
  useDialogAccessibility(showPanelDrawer, panelDrawerRef, () => setShowPanelDrawer(false));
  useDialogAccessibility(!!modal || showTerms, dialogRef, () => {
    if (actionLoading) return;
    if (showTerms) navigate("/rooms");
    else setModal(null);
  });
  // coupon state
  const [couponInput, setCouponInput]     = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // coupon object
  const [couponErr, setCouponErr]         = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  // review state
  const [reviewStars, setReviewStars]       = useState(5);
  const [reviewHover, setReviewHover]       = useState(0);
  const [reviewComment, setReviewComment]   = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [myReview, setMyReview]             = useState(null);
  // คะแนนรีวิวของคู่ค้า
  const [partnerRating, setPartnerRating]   = useState(null); // { avg, count } หรือ null
  const [chatImgFile, setChatImgFile]       = useState(null);
  const [chatImgLoading, setChatImgLoading] = useState(false);
  const chatImgInputRef = useRef(null);
  const msgsRef = useRef(null);
  const autoReleaseTriggered = useRef(false);
  const user = readCachedUser();
  // ชื่อที่แสดงในแชท — ใช้ชื่อจริงก่อน ถ้าไม่มีใช้ส่วนหน้า @ ของ email
  const displayName = user.name || user.displayName || user.email?.split("@")[0] || "ผู้ใช้";

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

  // ── Load room & handle joining ────────────────────────────────────────────
  useEffect(() => {
    if (!roomId || !user.uid) return;
    const doneLoad = perfMark("ChatPage: getDoc(rooms/" + roomId + ")");
    getDoc(doc(db, "rooms", roomId)).then(async (snap) => {
      if (!snap.exists()) { doneLoad("ไม่พบห้อง"); setLoading(false); return; }
      const data = { id: snap.id, ...snap.data() };
      setRoom(data);

      // อัปเดตชื่อปัจจุบันใน room ทุกครั้งที่เข้า (ป้องกันชื่อเก่าค้าง)
      if (!isAdminMode && data.members?.includes(user.uid)) {
        const nameUpdate = {};
        if (data.buyerId === user.uid)  nameUpdate.buyerName  = displayName;
        if (data.sellerId === user.uid) nameUpdate.sellerName = displayName;
        if (Object.keys(nameUpdate).length) updateDoc(doc(db, "rooms", roomId), nameUpdate).catch(() => {});
      }

      // Admin ไม่ต้อง join ห้อง
      if (!isAdminMode && !data.members?.includes(user.uid)) {
        // New joiner: check if other role is available and show terms
        const isBuyerTaken = !!data.buyerId;
        const isSellerTaken = !!data.sellerId;
        const availableRole = isBuyerTaken ? "seller" : isSellerTaken ? "buyer" : "buyer";

        // Check if terms already accepted (stored in localStorage)
        const alreadyAccepted = localStorage.getItem(TERMS_KEY(roomId));
        if (alreadyAccepted) {
          await joinRoom(data, JSON.parse(alreadyAccepted).role);
        } else {
          setJoinRole(availableRole);
          setPendingJoin(data);
          setShowTerms(true);
        }
      }
      setLoading(false);
      doneLoad("โหลดสำเร็จ");
    }).catch((err) => {
      console.error("[ChatPage] load room error:", err);
      doneLoad("error: " + (err.code || err.message));
      setLoading(false);
      if (err.code === "permission-denied" || err.code === "unauthenticated") {
        localStorage.removeItem("klang-user");
        navigate("/login");
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const joinRoom = async (data, role) => {
    const upd = { members: arrayUnion(user.uid) };
    if (!data.buyerId && role === "buyer") { upd.buyerId = user.uid; upd.buyerName = displayName; }
    else if (!data.sellerId && role === "seller") { upd.sellerId = user.uid; upd.sellerName = displayName; }
    else if (!data.buyerId) { upd.buyerId = user.uid; upd.buyerName = displayName; }
    else if (!data.sellerId) { upd.sellerId = user.uid; upd.sellerName = displayName; }
    await updateDoc(doc(db, "rooms", roomId), upd);
    await addDoc(collection(db, "rooms", roomId, "messages"), {
      text: `${displayName} เข้าสู่ห้องแชทในฐานะ${role === "buyer" ? "ผู้ซื้อ" : "ผู้ขาย"}`,
      type: "system", senderId: "system", createdAt: serverTimestamp(),
    });
  };

  const handleAcceptTerms = async () => {
    if (!termsAccepted) { setModalErr("กรุณายอมรับข้อกำหนดก่อน"); return; }
    setActionLoading(true);
    try {
      if (pendingJoin) await joinRoom(pendingJoin, joinRole);
      localStorage.setItem(TERMS_KEY(roomId), JSON.stringify({ role: joinRole, ts: Date.now() }));
      setShowTerms(false);
    } catch (_) { setModalErr("เข้าร่วมห้องไม่สำเร็จ กรุณาตรวจสอบว่าห้องยังว่างแล้วลองอีกครั้ง"); }
    finally { setActionLoading(false); }
  };

  // ── Real-time room updates ────────────────────────────────────────────────
  useEffect(() => {
    if (!roomId) return;
    const doneFirst = perfMark("ChatPage: onSnapshot(rooms/" + roomId + ") ครั้งแรก");
    let first = true;
    return onSnapshot(doc(db, "rooms", roomId), (snap) => {
      if (first) { doneFirst("exists=" + snap.exists()); first = false; }
      if (snap.exists()) setRoom({ id: snap.id, ...snap.data() });
    }, (err) => {
      if (first) { doneFirst("error: " + err.code); first = false; }
      console.error("[ChatPage] room listener error:", err);
      if (err.code === "permission-denied" || err.code === "unauthenticated") {
        localStorage.removeItem("klang-user");
        navigate("/login");
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // ── มาร์คแจ้งเตือน "ข้อความใหม่" ของห้องนี้เป็นอ่านแล้ว เมื่อเปิดห้องนี้อยู่ ──
  useEffect(() => {
    if (!user.uid || !roomId) return;
    const q = query(
      collection(db, "users", user.uid, "notifications"),
      where("type", "==", "message"),
      where("roomId", "==", roomId),
      where("read", "==", false)
    );
    getDocs(q).then((snap) => {
      snap.docs.forEach((d) => updateDoc(d.ref, { read: true }).catch(() => {}));
    }).catch(() => {});
  }, [roomId, user.uid]);

  // ── Admin: set adminReviewing flag ────────────────────────────────────────
  useEffect(() => {
    if (!isAdminMode || !roomId) return;
    updateDoc(doc(db, "rooms", roomId), { adminReviewing: true });
    return () => {
      updateDoc(doc(db, "rooms", roomId), { adminReviewing: false });
    };
  }, [isAdminMode, roomId]);

  // ── Real-time messages ────────────────────────────────────────────────────
  useEffect(() => {
    if (!roomId || (!isAdminMode && !room?.members?.includes(user.uid))) return;
    const doneFirst = perfMark("ChatPage: onSnapshot(messages) ครั้งแรก");
    let first = true;
    const q = query(collection(db, "rooms", roomId, "messages"), orderBy("createdAt", "asc"));
    return onSnapshot(q, (snap) => {
      if (first) { doneFirst(snap.size + " ข้อความ"); first = false; }
      setMsgs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => {
      if (first) { doneFirst("error: " + err.code); first = false; }
      console.error("[ChatPage] messages listener error:", err);
    });
  }, [roomId, isAdminMode, room?.members, user.uid]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    setTimeout(() => {
      if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }, 50);
  }, [msgs, typing]);

  // ── Wallet balance listener ───────────────────────────────────────────────
  useEffect(() => {
    if (!user.uid) return;
    return onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) setWalletBalance(snap.data().wallet_balance || 0);
    });
  }, [user.uid]);

  // ── Auto-release เมื่อครบกำหนดเวลายืนยัน (7 วันของจริง / 24 ชม. ดิจิทัล) ──
  // หมายเหตุ: escrowRelease อนุญาตเฉพาะผู้ซื้อ/แอดมิน — ถ้าคนที่เปิดแท็บอยู่ตอนครบกำหนด
  // เป็นผู้ขาย จะยังไม่ทำงาน (ต้องรอฝั่งผู้ซื้อเปิดแอป หรือ scheduled function ที่ยังไม่เปิดใช้)
  useEffect(() => {
    if (!room || room.status !== "shipped" || !room.shippedAt || autoReleaseTriggered.current) return;
    if (room.buyerId !== user.uid) return;
    const shippedDate = room.shippedAt.toDate?.();
    if (!shippedDate) return;
    const hoursSince = (Date.now() - shippedDate.getTime()) / (1000 * 60 * 60);
    const deadlineHours = room.dealType === "digital" ? 24 : 7 * 24;
    if (hoursSince >= deadlineHours) {
      autoReleaseTriggered.current = true;
      (async () => {
        try {
          await escrowReleaseFn({ roomId });
          await addDoc(collection(db, "rooms", roomId, "messages"), {
            text: room.dealType === "digital"
              ? "ครบ 24 ชั่วโมง ผู้ซื้อไม่กดยืนยัน — ระบบโอนเงินให้ผู้ขายอัตโนมัติแล้ว"
              : "ครบ 7 วัน ผู้ซื้อไม่กดยืนยัน — ระบบโอนเงินให้ผู้ขายอัตโนมัติแล้ว",
            type: "system", senderId: "system", createdAt: serverTimestamp(),
          });
        } catch {}
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, roomId]);

  // ── Load my existing review ───────────────────────────────────────────────
  useEffect(() => {
    if (!user.uid || !roomId) return;
    getDoc(doc(db, "reviews", `${roomId}_${user.uid}`)).then((snap) => {
      if (snap.exists()) setMyReview(snap.data());
    });
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load partner's overall rating ────────────────────────────────────────
  useEffect(() => {
    if (!room) return;
    const partnerId = room.buyerId === user.uid ? room.sellerId : room.buyerId;
    if (!partnerId) return;
    getDocs(query(collection(db, "reviews"), where("revieweeId", "==", partnerId)))
      .then((snap) => {
        if (snap.empty) { setPartnerRating({ avg: 0, count: 0 }); return; }
        const total = snap.docs.reduce((s, d) => s + (d.data().stars || 0), 0);
        setPartnerRating({ avg: total / snap.docs.length, count: snap.docs.length });
      })
      .catch(() => {});
  }, [room?.buyerId, room?.sellerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSys = async (text) =>
    addDoc(collection(db, "rooms", roomId, "messages"), {
      text, type: "system", senderId: "system", createdAt: serverTimestamp(),
    });

  // ── Send message ──────────────────────────────────────────────────────────
  const send = async () => {
    const text = input.trim();
    if (!text || typing || sendingRef.current) return;
    const draft = input;
    sendingRef.current = true;
    setSending(true);
    const isBuyer = room?.buyerId === user.uid;
    try {
      await addDoc(collection(db, "rooms", roomId, "messages"), {
      text, type: isAdminMode ? "admin" : isBuyer ? "buyer" : "seller",
      senderId: user.uid, senderName: isAdminMode ? "ผู้ดูแลระบบ" : displayName,
      createdAt: serverTimestamp(),
      });
      setInput(current => current === draft ? "" : current);
    } catch (_) {
      showToast("ส่งข้อความไม่สำเร็จ ข้อความของคุณยังอยู่ กรุณาลองอีกครั้ง");
      return;
    } finally { sendingRef.current = false; setSending(false); }
    if (!isAdminMode) {
      const partnerId = isBuyer ? room?.sellerId : room?.buyerId;
      if (partnerId) notif.newMessage(partnerId, displayName, text, roomId).catch(() => {});
    }
    const anthropicKey = process.env.REACT_APP_ANTHROPIC_KEY;
    if (!anthropicKey) return;
    setTyping(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": anthropicKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 400,
          system: `คุณคือ Klang AI ผู้ช่วยระบบ escrow สินค้า: ${room?.itemName} ราคา ฿${room?.price} สถานะ: ${STATUS_LABEL[room?.status] || ""} ตอบภาษาไทยสั้นกระชับ`,
          messages: [{ role: "user", content: text }],
        }),
      });
      const data = await res.json();
      const aiText = data.content?.[0]?.text || "ขอโทษครับ ไม่สามารถตอบได้ตอนนี้";
      await addDoc(collection(db, "rooms", roomId, "messages"), {
        text: aiText, type: "ai", senderId: "ai", senderName: "Klang AI",
        createdAt: serverTimestamp(),
      });
    } catch {}
    setTyping(false);
  };

  // ── Send image ────────────────────────────────────────────────────────────
  const sendImage = async (file) => {
    if (!file || chatImgLoading) return;
    setChatImgLoading(true);
    try {
      const ts = Date.now();
      const storageRef = ref(storage, `chat/${roomId}/${user.uid}_${ts}`);
      const task = uploadBytesResumable(storageRef, file);
      const url = await new Promise((resolve, reject) => {
        task.on("state_changed", null, reject, () =>
          getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
        );
      });
      const buyer = room?.buyerId === user.uid;
      await addDoc(collection(db, "rooms", roomId, "messages"), {
        type: "image",
        role: isAdminMode ? "admin" : buyer ? "buyer" : "seller",
        senderId: user.uid,
        senderName: isAdminMode ? "ผู้ดูแลระบบ" : displayName,
        imageUrl: url,
        createdAt: serverTimestamp(),
      });
      if (!isAdminMode) {
        const partnerId = buyer ? room?.sellerId : room?.buyerId;
        if (partnerId) notif.newMessage(partnerId, displayName, "ส่งรูปภาพ", roomId).catch(() => {});
      }
    } catch (err) {
      showToast("อัปโหลดรูปไม่สำเร็จ");
      console.error(err);
    }
    setChatImgFile(null);
    setChatImgLoading(false);
  };

  // ── Pay ───────────────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!room.sellerId) { setModalErr("ยังไม่มีผู้ขายเข้าร่วมห้อง กรุณารอก่อน"); return; }
    setActionLoading(true); setModalErr("");
    try {
      const { data } = await escrowLockFn({ roomId, couponCode: appliedCoupon?.code || null });
      const { originalFee, discount, fee: paidFee, total } = data;

      const feeText = discount > 0
        ? `ค่าธรรมเนียม ฿${originalFee.toLocaleString()} ลด ฿${discount.toLocaleString()} = ฿${paidFee.toLocaleString()}`
        : `ค่าธรรมเนียม ฿${paidFee.toLocaleString()}`;
      const couponText = appliedCoupon ? ` (ใช้คูปอง ${appliedCoupon.code})` : "";

      await addSys(
        `ผู้ซื้อชำระเงิน ฿${total.toLocaleString()} เข้ากองกลางแล้ว (ราคา ฿${(room.price).toLocaleString()} + ${feeText})${couponText} — รอผู้ขายจัดส่งสินค้า`
      );
      setModal(null);
      clearCoupon();
      showToast("ชำระเงินสำเร็จ");
    } catch (err) { setModalErr(err.message || "ชำระเงินไม่สำเร็จ"); }
    setActionLoading(false);
  };

  // ── Upload file with progress ─────────────────────────────────────────────
  const uploadWithProgress = (file, path, key) =>
    new Promise((resolve, reject) => {
      if (!file) { resolve(null); return; }
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);
      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadProgress((prev) => ({ ...prev, [key]: pct }));
        },
        (err) => {
          console.error(`[Storage Error][${key}]`, err.code, err.message);
          reject(err);
        },
        () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
      );
    });

  // ── Ship ──────────────────────────────────────────────────────────────────
  const handleShip = async () => {
    if (!tracking.trim()) { setModalErr("กรุณากรอกเลขพัสดุ"); return; }
    if (!courier.trim()) { setModalErr("กรุณาเลือกบริษัทขนส่ง"); return; }
    if (videoFiles.length === 0) { setModalErr("กรุณาแนบวีดีโอสินค้าอย่างน้อย 1 ไฟล์ (บังคับ เพื่อป้องกันการโกง)"); return; }
    if (photoFiles.length === 0) { setModalErr("กรุณาแนบรูปกล่องสินค้าอย่างน้อย 1 รูป (บังคับ เพื่อป้องกันการโกง)"); return; }
    const oversizeVideo = videoFiles.find((f) => f.size > 200 * 1024 * 1024);
    if (oversizeVideo) { setModalErr(`วีดีโอ "${oversizeVideo.name}" ใหญ่เกิน 200 MB กรุณาบีบอัดไฟล์ก่อน`); return; }
    const oversizePhoto = photoFiles.find((f) => f.size > 10 * 1024 * 1024);
    if (oversizePhoto) { setModalErr(`รูป "${oversizePhoto.name}" ใหญ่เกิน 10 MB กรุณาย่อขนาดรูปก่อน`); return; }
    setActionLoading(true); setModalErr("");
    setUploadProgress({});
    try {
      setUploadingPhotos(true);
      const ts = Date.now();
      const [videoUrls, photoUrls] = await Promise.all([
        Promise.all(videoFiles.map((f, i) =>
          uploadWithProgress(f, `shipping/${roomId}/video_${ts}_${i}`, `video_${i}`))),
        Promise.all(photoFiles.map((f, i) =>
          uploadWithProgress(f, `shipping/${roomId}/box_${ts}_${i}`, `photo_${i}`))),
      ]);
      setUploadingPhotos(false);

      await updateDoc(doc(db, "rooms", roomId), {
        status: "shipped",
        trackingNumber: tracking.trim(),
        courierName: courier.trim(),
        shippedAt: serverTimestamp(),
        videoUrls,
        photoUrls,
      });

      // ส่งข้อความ system ธรรมดา
      await addSys(`ผู้ขายจัดส่งสินค้าแล้ว (วีดีโอ ${videoUrls.length} ไฟล์, รูป ${photoUrls.length} รูป) — ผู้ซื้อกรุณายืนยันรับของภายใน 7 วัน`);

      // ส่งข้อความ shipping card เข้า chat ให้อีกฝ่ายเห็น
      await addDoc(collection(db, "rooms", roomId, "messages"), {
        type: "shipping",
        senderId: user.uid,
        senderName: displayName,
        courier: courier.trim(),
        tracking: tracking.trim(),
        videoUrls,
        photoUrls,
        createdAt: serverTimestamp(),
      });

      setModal(null); setTracking(""); setCourier(""); setVideoFiles([]); setPhotoFiles([]);
      showToast("แจ้งจัดส่งสำเร็จ");
    } catch (err) {
      setUploadingPhotos(false);
      console.error("[handleShip error]", err);
      const msg =
        err.code === "storage/unauthorized"  ? "Storage Rules บล็อกอยู่ — ต้องแก้ Rules ใน Firebase Console ก่อน" :
        err.code === "storage/unauthenticated" ? "ยังไม่ได้ล็อกอิน กรุณาล็อกอินใหม่" :
        err.code === "storage/quota-exceeded"  ? "พื้นที่ Firebase Storage เต็ม" :
        err.code === "storage/canceled"        ? "อัปโหลดถูกยกเลิก" :
        `${err.code || ""} — ${err.message || "ลองใหม่"}`;
      setModalErr(msg);
    }
    setActionLoading(false);
  };

  // ── Deliver (สินค้าดิจิทัล) ──────────────────────────────────────────────
  // ส่งรหัส/ไอดี/บัญชีเป็นข้อความในแชทแทนการแจ้งเลขพัสดุ — ไม่มีไฟล์ให้อัปโหลด
  // จึงไม่มี network call ที่ล้มเหลวได้เหมือน handleShip (ไม่ต้องดัก storage error)
  const handleDeliver = async () => {
    const id = deliveryId.trim();
    const pw = deliveryPw.trim();
    const note = deliveryNote.trim();
    if (!id && !pw && !note) { setModalErr("กรุณากรอกข้อมูลที่จะส่งมอบอย่างน้อย 1 ช่อง"); return; }
    setActionLoading(true); setModalErr("");
    try {
      await updateDoc(doc(db, "rooms", roomId), {
        status: "shipped",
        shippedAt: serverTimestamp(),
      });

      await addSys(`ผู้ขายส่งมอบโค้ด/บัญชีแล้ว — ผู้ซื้อกรุณาตรวจสอบและยืนยันภายใน 24 ชั่วโมง`);

      // ส่งเนื้อหาที่ส่งมอบเป็นข้อความในแชท (แสดงเป็นการ์ดมีปุ่มคัดลอก) — เป็นหลักฐานให้แอดมินเห็นได้ตรงๆ ถ้ามีข้อพิพาท
      // text = สำรองเป็นข้อความธรรมดาไว้ให้ตัวที่ไม่รู้จักชนิดข้อความนี้
      await addDoc(collection(db, "rooms", roomId, "messages"), {
        type: "delivery",
        delivery: { id, password: pw, note },
        text: [id && `ไอดี: ${id}`, pw && `รหัสผ่าน: ${pw}`, note && `อื่นๆ: ${note}`].filter(Boolean).join("\n"),
        senderId: user.uid,
        senderName: isAdminMode ? "ผู้ดูแลระบบ" : displayName,
        createdAt: serverTimestamp(),
      });

      setModal(null); setDeliveryId(""); setDeliveryPw(""); setDeliveryNote(""); setShowDeliveryPw(false);
      showToast("ส่งมอบสำเร็จ");
    } catch (err) {
      console.error("[handleDeliver error]", err);
      setModalErr("เกิดข้อผิดพลาด: " + (err?.message || "กรุณาลองใหม่"));
    }
    setActionLoading(false);
  };

  // ── Confirm receipt (buyer) ───────────────────────────────────────────────
  const handleConfirmReceive = async () => {
    setActionLoading(true); setModalErr("");
    try {
      // escrowRelease เปลี่ยนสถานะห้องเป็น "released" + โอนเงินให้ผู้ขายแบบ atomic ในทีเดียว
      await escrowReleaseFn({ roomId });
      await addSys(`ผู้ซื้อยืนยันรับสินค้าแล้ว — Klang โอนเงิน ฿${room.price.toLocaleString()} ให้ผู้ขายเรียบร้อย`);
      setModal(null); showToast("ยืนยันรับของสำเร็จ");
    } catch (err) {
      console.error("escrowRelease error:", err);
      setModalErr("เกิดข้อผิดพลาด: " + (err?.message || "กรุณาลองใหม่"));
    }
    setActionLoading(false);
  };

  // ── Dispute ───────────────────────────────────────────────────────────────
  const handleDispute = async () => {
    if (!dispute.trim()) { setModalErr("กรุณาอธิบายปัญหา"); return; }
    setActionLoading(true);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "rooms", roomId), { status: "disputed" });
      batch.set(doc(collection(db, "rooms", roomId, "messages")), {
        type: "system", senderId: user.uid,
        text: `แจ้งข้อพิพาท: ${dispute} — ทีมงานจะตรวจสอบรายละเอียดและหลักฐานในดีล`,
        createdAt: serverTimestamp(),
      });
      await batch.commit();
      setModal(null); setDispute("");
    } catch (err) { setModalErr(err?.message || "แจ้งปัญหาไม่สำเร็จ กรุณาลองอีกครั้ง"); }
    setActionLoading(false);
  };

  const copyCode = () => {
    if (room?.roomCode) {
      navigator.clipboard.writeText(room.roomCode);
      showToast(`คัดลอกรหัส ${room.roomCode} แล้ว ✓`);
    }
  };

  // ── Coupon ────────────────────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponErr("");
    setCouponLoading(true);
    try {
      const c = await validateCoupon(couponInput, user.uid, room.price || 0);
      setAppliedCoupon(c);
      setCouponErr("");
    } catch (err) {
      setCouponErr(err.message);
      setAppliedCoupon(null);
    }
    setCouponLoading(false);
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponErr("");
  };

  // ── Loading / not found ───────────────────────────────────────────────────
  if (loading)
    return (
      <div className="cp-loading" style={{ background: "#f5f6f8" }}>
        <style>{css}</style>
        <div className="cp-spinner" /><span>กำลังโหลด...</span>
      </div>
    );

  if (!room)
    return (
      <div className="cp-loading" style={{ background: "#f5f6f8" }}>
        <style>{css}</style>
        <div style={{ color: "var(--text-muted)" }}><Icon name="frown" size={40} /></div>
        <span>ไม่พบห้องแชทนี้</span>
        <button onClick={() => navigate("/home")} style={{ marginTop: 12, padding: "8px 20px", background: "#dc3728", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: "Prompt", fontWeight: 600 }}>
          กลับหน้าหลัก
        </button>
      </div>
    );

  const isBuyer = room.buyerId === user.uid;
  const isSeller = room.sellerId === user.uid;
  const isDigital = room.dealType === "digital"; // สินค้าดิจิทัล — ข้ามขั้นตอนแจ้งเลขพัสดุ/รูปแพ็ก
  const receiveChecklist = isDigital
    ? ["ทดสอบรหัส/ไอดี/บัญชีแล้วใช้งานได้จริง", "ไม่มีการล็อกสิทธิ์หรือเปลี่ยนรหัสผ่านภายหลัง"]
    : ["สินค้าครบถ้วนตามที่สั่ง", "สภาพสินค้าสมบูรณ์ ไม่เสียหาย", "สินค้าตรงตามที่ระบุในโฆษณา"];
  const step = STATUS_STEP[room.status] ?? 0;
  // รองรับดีลเก่าที่เคยเก็บเป็นไฟล์เดียว (videoProduct/photoBox) ควบคู่กับดีลใหม่ที่เก็บเป็น array
  const roomVideoUrls = room.videoUrls?.length ? room.videoUrls : (room.videoProduct ? [room.videoProduct] : []);
  const roomPhotoUrls = room.photoUrls?.length ? room.photoUrls : (room.photoBox ? [room.photoBox] : []);
  const fee = room.platformFee ?? calcFee(room.price || 0);
  const total = room.lockedAmount ?? ((room.price || 0) + fee);
  // คำนวณค่าธรรมเนียมที่รวม coupon แล้ว (ใช้ใน pay modal)
  const { originalFee, discount: couponDiscount, finalFee } =
    calcDiscountedFee(room.price || 0, appliedCoupon);
  const discountedTotal = (room.price || 0) + finalFee;

  // ── เวลาตั้งแต่ส่งของ + กำหนดเวลายืนยัน ─────────────────────────────────
  // สินค้าดิจิทัลส่งถึงทันที ไม่มีช่วงรอขนส่ง จึงให้เวลายืนยันสั้นกว่าสินค้าจริง
  const AUTO_RELEASE_HOURS = isDigital ? 24 : 7 * 24;
  const hoursSinceShipped = room.shippedAt?.toDate
    ? (Date.now() - room.shippedAt.toDate().getTime()) / (1000 * 60 * 60)
    : 0;
  const daysSinceShipped = Math.floor(hoursSinceShipped / 24); // ใช้กับสินค้าจริง (หน่วยวัน) เหมือนเดิม
  const hoursLeftToConfirm = Math.max(0, Math.ceil(AUTO_RELEASE_HOURS - hoursSinceShipped));

  // ── Review handler ────────────────────────────────────────────────────────
  const handleReview = async () => {
    if (!reviewStars) return;
    setReviewSubmitting(true);
    try {
      const partnerId   = isBuyer ? room.sellerId   : room.buyerId;
      const partnerName = isBuyer ? (room.sellerName || "ผู้ขาย") : (room.buyerName || "ผู้ซื้อ");
      const data = {
        roomId,        roomCode:      room.roomCode || "",
        reviewerId:    user.uid,       reviewerName:  displayName,
        revieweeId:    partnerId,      revieweeName:  partnerName,
        stars:    reviewStars,
        comment:  reviewComment.trim(),
        role:     isBuyer ? "buyer" : "seller",
        itemName: room.itemName || "",
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, "reviews", `${roomId}_${user.uid}`), data);
      setMyReview(data);
      setModal(null);
      showToast("ส่งรีวิวแล้ว ขอบคุณ!");
    } catch (err) {
      showToast("เกิดข้อผิดพลาด: " + err.message);
    }
    setReviewSubmitting(false);
  };

  // ── Escrow actions ────────────────────────────────────────────────────────
  const renderEscrowActions = () => {
    const s = room.status;
    if (s === "released" || s === "completed")
      return (
        <>
          <button className="cp-abtn done">ดีลเสร็จสิ้นแล้ว</button>
          {(isBuyer || isSeller) && !myReview && (
            <button className="cp-abtn"
              style={{background:'rgba(255,204,0,0.1)',color:'#ffcc00',border:'1px solid rgba(255,204,0,0.3)',marginTop:4}}
              onClick={() => { setReviewStars(5); setReviewHover(0); setReviewComment(""); setModal("review"); }}>
              ให้คะแนนคู่ค้า
            </button>
          )}
          {myReview && (
            <div className="cp-review-done">
              คุณรีวิวแล้ว — {myReview.stars}/5 ดาว — ขอบคุณ!
            </div>
          )}
        </>
      );
    if (s === "disputed") {
      if (isAdminMode) return (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={{padding:'10px 14px',background:'rgba(255,179,71,0.08)',border:'1px solid rgba(255,179,71,0.3)',borderRadius:10,fontSize:'0.78rem',color:'#c77b27',textAlign:'center'}}>
            โหมด Admin — กำลังตรวจสอบข้อพิพาท
          </div>
          <button className="cp-abtn pay" style={{background:'#dc3728'}}
            onClick={async () => {
              if (!window.confirm("โอนเงินให้ผู้ขายและปิดดีลนี้?")) return;
              try {
                await escrowReleaseFn({ roomId });
                await addSys("Admin ตัดสิน: โอนเงินให้ผู้ขายแล้ว — ดีลปิดแล้ว");
                showToast("โอนเงินให้ผู้ขายแล้ว");
              } catch (err) { showToast("" + (err.message || "เกิดข้อผิดพลาด")); }
            }}>
            ตัดสิน: โอนเงินให้ผู้ขาย
          </button>
          <button className="cp-abtn" style={{background:'rgba(255,85,85,0.15)',color:'#ff5555',border:'1px solid rgba(255,85,85,0.3)'}}
            onClick={async () => {
              if (!window.confirm("คืนเงินให้ผู้ซื้อและปิดดีลนี้?")) return;
              try {
                await escrowRefundFn({ roomId, reason: "แอดมินตัดสินข้อพิพาท — คืนเงินผู้ซื้อ" });
                await addSys("Admin ตัดสิน: คืนเงินให้ผู้ซื้อแล้ว — ดีลปิดแล้ว");
                showToast("คืนเงินให้ผู้ซื้อแล้ว");
              } catch (err) { showToast("" + (err.message || "เกิดข้อผิดพลาด")); }
            }}>
            ตัดสิน: คืนเงินให้ผู้ซื้อ
          </button>
          <button className="cp-abtn" style={{background:'transparent',border:'1px solid var(--border)',color:'var(--text-muted)',fontSize:'0.78rem'}}
            onClick={() => navigate("/admin")}>
            ← กลับแผงควบคุม Admin
          </button>
        </div>
      );
      return room.adminReviewing
        ? <div className="cp-wait-box" style={{background:'rgba(255,179,71,0.08)',border:'1px solid rgba(255,179,71,0.3)',color:'#c77b27'}}>
            Admin กำลังตรวจสอบข้อพิพาท — กรุณารอผลการตัดสิน
          </div>
        : <div className="cp-wait-box">อยู่ระหว่างตรวจสอบข้อพิพาท — กรุณารอ Admin ตรวจสอบ</div>;
    }
    return (
      <>
        {isBuyer && s === "created" && !room.sellerId && (
          <div className="cp-wait-box" style={{borderColor:"rgba(255,179,71,0.3)",color:"var(--warning)"}}>
            รอผู้ขายเข้าร่วมห้องก่อน จึงจะชำระเงินได้
          </div>
        )}
        {isBuyer && s === "created" && room.sellerId && (
          <button className="cp-abtn pay" onClick={() => { setModalErr(""); setModal("pay"); }}>
            ชำระเงิน ฿{total.toLocaleString()}
          </button>
        )}
        {isBuyer && s === "deposited" && (
          <div className="cp-wait-box">{isDigital ? "รอผู้ขายส่งมอบโค้ด/บัญชี" : "รอผู้ขายจัดส่งสินค้า"}</div>
        )}
        {isBuyer && s === "shipped" && (
          <>
            {(isDigital ? hoursLeftToConfirm <= 6 : daysSinceShipped >= 5) && (
              <div className="cp-auto-release-banner">
                {isDigital
                  ? `เหลือ ${hoursLeftToConfirm} ชั่วโมง ก่อนโอนเงินอัตโนมัติ`
                  : `เหลือ ${7 - daysSinceShipped} วัน ก่อนโอนเงินอัตโนมัติ`}
              </div>
            )}
            <button className="cp-abtn receive" onClick={() => { setModalErr(""); setCheckItems(new Array(receiveChecklist.length).fill(false)); setModal("receive"); }}>
              ยืนยันรับสินค้า
            </button>
            <button className="cp-abtn claim" onClick={() => { setModalErr(""); setModal("dispute"); }}>
              แจ้งปัญหาในดีล
            </button>
          </>
        )}
        {isBuyer && s === "confirmed" && (
          <div className="cp-wait-box">ยืนยันแล้ว — กำลังโอนเงินให้ผู้ขาย</div>
        )}

        {isSeller && s === "created" && (
          <div className="cp-wait-box">รอผู้ซื้อชำระเงิน</div>
        )}
        {isSeller && s === "deposited" && (
          <button className="cp-abtn ship" onClick={() => { setModalErr(""); setModal(isDigital ? "deliver" : "ship"); }}>
            {isDigital ? "ส่งมอบโค้ด/บัญชี" : "แจ้งจัดส่งสินค้า"}
          </button>
        )}
        {isSeller && s === "shipped" && (
          <div className="cp-wait-box">
            {isDigital
              ? `รอผู้ซื้อยืนยัน (เหลือ ${hoursLeftToConfirm} ชม.)`
              : `รอผู้ซื้อยืนยันรับของ (${daysSinceShipped}/7 วัน)`}
          </div>
        )}
        {isSeller && s === "confirmed" && (
          <div className="cp-wait-box">ผู้ซื้อยืนยันแล้ว — กำลังโอนเงินให้คุณ</div>
        )}

        {!isBuyer && !isSeller && (
          <div className="cp-wait-box">ไม่ได้เป็นสมาชิกของห้องนี้</div>
        )}
      </>
    );
  };

  // ── Render shipping evidence (วีดีโอ + รูปกล่อง — แนบได้หลายไฟล์) ────────
  const renderShipPhotos = () => {
    const totalFiles = videoFiles.length + photoFiles.length;
    const overallPct = totalFiles > 0
      ? Math.round(Object.values(uploadProgress).reduce((s, v) => s + v, 0) / totalFiles)
      : 0;
    return (
      <div>
        <div className="cp-photo-group">
          <label className="cp-photo-label">
            วีดีโอสินค้า <span style={{color:"var(--danger)",fontWeight:600}}>*บังคับ อย่างน้อย 1 ไฟล์</span>
          </label>
          <input type="file" accept="video/*" multiple className="cp-photo-input"
            disabled={uploadingPhotos}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) setVideoFiles((prev) => [...prev, ...files].slice(0, MAX_SHIP_FILES));
              e.target.value = "";
            }} />
          {videoFiles.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {videoFiles.map((f, i) => (
                <div key={i} className="cp-file-item">
                  <div className="cp-file-size">
                    {f.name} — {(f.size / (1024*1024)).toFixed(1)} MB
                    {f.size > 100*1024*1024 && <span style={{color:"var(--warning)",marginLeft:6}}>ไฟล์ใหญ่ อาจใช้เวลานาน</span>}
                  </div>
                  <video
                    style={{width:"100%",height:90,objectFit:"cover",borderRadius:8,marginTop:4,border:"1px solid var(--border)",background:"#000"}}
                    src={URL.createObjectURL(f)} controls muted playsInline
                  />
                  {!uploadingPhotos && (
                    <button type="button" className="cp-file-remove" style={{ top: 26 }}
                      onClick={() => setVideoFiles((prev) => prev.filter((_, idx) => idx !== i))}>✕</button>
                  )}
                </div>
              ))}
            </div>
          )}
          {videoFiles.length < MAX_SHIP_FILES && (
            <div className="cp-file-add-hint">เลือกได้ทีละหลายไฟล์ — เพิ่มได้อีก {MAX_SHIP_FILES - videoFiles.length} ไฟล์</div>
          )}
        </div>
        <div className="cp-photo-group">
          <label className="cp-photo-label">
            รูปกล่องสินค้า <span style={{color:"var(--danger)",fontWeight:600}}>*บังคับ อย่างน้อย 1 รูป</span>
          </label>
          <input type="file" accept="image/*" multiple className="cp-photo-input"
            disabled={uploadingPhotos}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) setPhotoFiles((prev) => [...prev, ...files].slice(0, MAX_SHIP_FILES));
              e.target.value = "";
            }} />
          {photoFiles.length > 0 && (
            <div className="cp-photo-grid">
              {photoFiles.map((f, i) => (
                <div key={i} className="cp-file-item">
                  <img className="cp-photo-prev" src={URL.createObjectURL(f)} alt={`box-${i}`} />
                  {!uploadingPhotos && (
                    <button type="button" className="cp-file-remove"
                      onClick={() => setPhotoFiles((prev) => prev.filter((_, idx) => idx !== i))}>✕</button>
                  )}
                </div>
              ))}
            </div>
          )}
          {photoFiles.length < MAX_SHIP_FILES && (
            <div className="cp-file-add-hint">เลือกได้ทีละหลายรูป — เพิ่มได้อีก {MAX_SHIP_FILES - photoFiles.length} รูป</div>
          )}
        </div>
        {uploadingPhotos && (
          <div className="cp-upload-progress">
            <div className="cp-upload-bar-wrap">
              <div className="cp-upload-bar" style={{width:`${overallPct}%`}} />
            </div>
            <div className="cp-upload-pct"><span>กำลังอัปโหลด {totalFiles} ไฟล์...</span><span>{overallPct}%</span></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`cp kl-chat${chatHeight < 600 ? " kl-short" : ""}`} style={{"--chat-height":`${Math.round(chatHeight)}px`}}>
      <style>{css}</style>

      {/* ── Admin Banner ── */}
      {isAdminMode && (
        <div style={{background:'rgba(255,179,71,0.1)',borderBottom:'1px solid rgba(255,179,71,0.3)',padding:'8px 20px',fontSize:'0.78rem',color:'#c77b27',display:'flex',alignItems:'center',gap:8}}>
          <strong>โหมด Admin</strong> — คุณกำลังดูห้องนี้ในฐานะผู้ดูแลระบบ
          <button onClick={() => navigate("/admin")} style={{marginLeft:'auto',background:'none',border:'1px solid rgba(255,179,71,0.4)',color:'#c77b27',borderRadius:6,padding:'3px 10px',cursor:'pointer',fontSize:'0.72rem',fontFamily:'Prompt,sans-serif'}}>← Admin Panel</button>
        </div>
      )}

      {/* ── Header ── */}
      <div className="cp-top">
        <BackButton fallback={isAdminMode ? "/admin" : "/rooms"} compact />
        <button className="kl-wordmark kl-chat-logo" onClick={() => navigate("/home")}>klang.</button>
        <div className="cp-info">
          <div className="cp-id">รหัส: {room.roomCode} · {isAdminMode ? "ผู้ดูแลระบบ" : isBuyer ? "ผู้ซื้อ" : isSeller ? "ผู้ขาย" : ""}</div>
          <div className="cp-name">ห้องซื้อขาย / {room.itemName}</div>
          <div className="cp-price">฿{(room.price || 0).toLocaleString()} · ค่าธรรมเนียม {(room.price || 0) < 1000 ? "฿50 คงที่" : "฿100 คงที่"} (฿{fee.toLocaleString()})</div>
        </div>
        <span className={`cp-sbadge ${room.status}`}>{STATUS_LABEL[room.status] || room.status}</span>
        <button className="kl-chat-copy" onClick={async () => {
          try { await navigator.clipboard.writeText(`${window.location.origin}/chat/${roomId}`); showToast("คัดลอกลิงก์ห้องแล้ว"); }
          catch (_) { showToast("คัดลอกไม่สำเร็จ กรุณาคัดลอกลิงก์จากแถบที่อยู่"); }
        }}>คัดลอกลิงก์ห้อง</button>
        <NotificationBell />
        {/* Info toggle (mobile only) */}
        <button className="cp-info-toggle" onClick={() => setShowPanelDrawer(true)} title="ข้อมูลดีล"><Icon name="info" size={18} /></button>
      </div>

      {/* ── Room code bar ── */}
      <div className="cp-codebar">
        <div>
          <span className="cp-codelabel">รหัสห้อง: </span>
          <span className="cp-codeval">{room.roomCode}</span>
          <span className="cp-codelabel">ส่งให้อีกฝ่ายเพื่อเข้าร่วม</span>
        </div>
        <button className="cp-copybtn" onClick={copyCode}>คัดลอก</button>
      </div>

      {/* ── Progress bar ── */}
      <div className="cp-prog">
        <div className="cp-steps">
          {STEPS.map((label, i) => (
            <div key={i} className={`cp-step${i < step ? " done" : i === step ? " active" : ""}`}>
              <div className="cp-circle">{i < step ? "✓" : i + 1}</div>
              <div><div className="cp-slabel">{label}</div><span className="kl-step-caption">{i < step ? "สำเร็จแล้ว" : i === step ? "ขั้นตอนปัจจุบัน" : "รอดำเนินการ"}</span></div>
            </div>
          ))}
        </div>
      </div>

      <div className="cp-body">
        {/* ── Sidebar (desktop) ── */}
        <div className="cp-panel">
          <div className="cp-psec">
            <div className="cp-ptitle">สรุปดีล</div>
            <div className="kl-chat-item"><Icon name={isDigital ? "gamepad" : "box"} size={32} /><div><strong>{room.itemName}</strong><span>{isDigital ? "ส่งมอบผ่านแชท" : "สินค้าจัดส่งพัสดุ"}</span></div></div>
            {[
              ["ราคาสินค้า", `฿${(room.price || 0).toLocaleString()}`, "g"],
              ["ค่าธรรมเนียมผู้ซื้อ", `฿${fee.toLocaleString()}`],
              ["ยอดที่ผู้ซื้อชำระ", `฿${total.toLocaleString()}`, "g"],
              ...(room.courierName ? [["บริษัทขนส่ง", room.courierName, "b"]] : []),
              ...(room.trackingNumber ? [["เลขพัสดุ", room.trackingNumber, "b"]] : []),
            ].map(([l, v, c]) => (
              <div key={l} className="cp-irow">
                <span className="cp-rl">{l}</span>
                <span className={`cp-rv${c ? " " + c : ""}`}>{v}</span>
              </div>
            ))}
            <div className="kl-escrow-note"><strong>{["deposited","shipped","disputed"].includes(room.status) ? "พักเงินไว้ในดีลแล้ว" : STATUS_LABEL[room.status] || room.status}</strong><span>{room.status === "released" ? "ระบบปล่อยเงินให้ผู้ขายแล้ว" : "เงินจะปล่อยเมื่อผู้ซื้อยืนยันรับสินค้า"}</span></div>
            {roomVideoUrls.map((url, i) => (
              <a key={"v"+i} href={url} target="_blank" rel="noreferrer"
                style={{ display: "block", fontSize: "0.72rem", color: "var(--accent)", marginTop: i === 0 ? 6 : 4 }}>
                ดูวีดีโอสินค้า{roomVideoUrls.length > 1 ? ` #${i + 1}` : ""}
              </a>
            ))}
            {roomPhotoUrls.map((url, i) => (
              <a key={"p"+i} href={url} target="_blank" rel="noreferrer"
                style={{ display: "block", fontSize: "0.72rem", color: "var(--accent)", marginTop: 4 }}>
                ดูรูปกล่องสินค้า{roomPhotoUrls.length > 1 ? ` #${i + 1}` : ""}
              </a>
            ))}
          </div>
          <div className="cp-psec">
            <div className="cp-ptitle">คู่ค้าในห้อง</div>
            {room.buyerId ? (
              <div className="cp-member">
                <div className="cp-mav" style={{ background: "linear-gradient(135deg,#1a3a6a,#2a5a9a)", color: "#607caa" }}><Icon name="cart" size={18} /></div>
                <div>
                  <div className="cp-mname">{room.buyerName || "ผู้ซื้อ"}</div>
                  <div className="cp-mrole">ผู้ซื้อ{room.buyerId === user.uid && !isAdminMode ? " (คุณ)" : ""}</div>
                  {/* แสดง rating เฉพาะคู่ค้า (ไม่ใช่ตัวเอง) */}
                  {room.buyerId !== user.uid && partnerRating !== null && (
                    partnerRating.count > 0
                      ? <div className="cp-rating-badge"><Icon name="star-fill" size={12} /> {partnerRating.avg.toFixed(1)} <span style={{opacity:0.6}}>({partnerRating.count} รีวิว)</span></div>
                      : <div className="cp-rating-badge no-review">ยังไม่มีรีวิว</div>
                  )}
                </div>
              </div>
            ) : <div className="cp-waiting">รอผู้ซื้อเข้าร่วม</div>}
            {room.sellerId ? (
              <div className="cp-member">
                <div className="cp-mav" style={{ background: "linear-gradient(135deg,#1a3a25,#2a5a35)", color: "#dc3728" }}><Icon name="briefcase" size={18} /></div>
                <div>
                  <div className="cp-mname">{room.sellerName || "ผู้ขาย"}</div>
                  <div className="cp-mrole">ผู้ขาย{room.sellerId === user.uid && !isAdminMode ? " (คุณ)" : ""}</div>
                  {/* แสดง rating เฉพาะคู่ค้า (ไม่ใช่ตัวเอง) */}
                  {room.sellerId !== user.uid && partnerRating !== null && (
                    partnerRating.count > 0
                      ? <div className="cp-rating-badge"><Icon name="star-fill" size={12} /> {partnerRating.avg.toFixed(1)} <span style={{opacity:0.6}}>({partnerRating.count} รีวิว)</span></div>
                      : <div className="cp-rating-badge no-review">ยังไม่มีรีวิว</div>
                  )}
                </div>
              </div>
            ) : <div className="cp-waiting">รอผู้ขายเข้าร่วม</div>}
            {isAdminMode && (
              <div className="cp-member" style={{ background: "rgba(255,179,71,0.06)", border: "1px solid rgba(255,179,71,0.2)" }}>
                <div className="cp-mav" style={{ background: "linear-gradient(135deg,#3a2a0a,#5a4a1a)", color: "#c77b27" }}><Icon name="shield" size={18} /></div>
                <div>
                  <div className="cp-mname" style={{ color: "#c77b27" }}>ผู้ดูแลระบบ</div>
                  <div className="cp-mrole">Admin (คุณ)</div>
                </div>
              </div>
            )}
          </div>
          <div className="cp-escrow-sec">
            <div className="cp-escrow-title">{room.status === "shipped" ? "ตรวจสอบสินค้าก่อนยืนยัน" : "การดำเนินการ"}</div>
            {room.status === "shipped" && <p className="kl-chat-small">เมื่อยืนยัน ระบบจะปล่อยเงินให้ผู้ขาย<br />หากมีปัญหา ให้แจ้งก่อนหมดเวลายืนยัน</p>}
            {renderEscrowActions()}
          </div>
        </div>

        {/* ── Chat ── */}
        <div className="cp-chat">
          <header className="kl-conversation-header"><strong>บทสนทนา</strong><span>พูดคุยและเก็บหลักฐานในห้องนี้</span><b>ดีล #{room.roomCode}</b></header>
          <div className="cp-msgs" ref={msgsRef}>
            <div className="kl-chat-date">{(msgs[0]?.createdAt?.toDate?.() || new Date()).toLocaleDateString("th-TH", {day:"numeric",month:"long",year:"numeric"})}</div>
            {msgs.length === 0 && (
              <div style={{ textAlign: "center", color: "#88919e", fontSize: "0.83rem", marginTop: 32 }}>
                ยังไม่มีข้อความ เริ่มสนทนาได้เลย
              </div>
            )}
            {msgs.map((m) => {
              if (m.type === "system")
                return <div key={m.id} className="cp-sys">{stripEmoji(m.text)}</div>;

              // ── Shipping card ────────────────────────────────────────────
              if (m.type === "shipping") {
                const isRight = m.senderId === user.uid;
                return (
                  <div key={m.id} className={`cp-mrow${isRight ? " right" : ""}`}>
                    <div className="cp-av" style={{ background: "linear-gradient(135deg,#3a2a1a,#5a4a2a)", color: "#c77b27" }}><Icon name="box" size={18} /></div>
                    <div>
                      <div className="cp-bname">{m.senderName}</div>
                      <div className="cp-ship-card">
                        <div className="cp-ship-card-title">ข้อมูลการจัดส่ง</div>
                        <div className="cp-ship-card-row">
                          <span className="cp-ship-card-label">ขนส่ง</span>
                          <span className="cp-ship-card-val">{m.courier}</span>
                        </div>
                        <div className="cp-ship-card-row">
                          <span className="cp-ship-card-label">เลขพัสดุ</span>
                          <span className="cp-ship-card-val tracking">{m.tracking}</span>
                        </div>
                        <div className="cp-ship-card-media">
                          {(() => {
                            // รองรับข้อความเก่าที่เก็บเป็นไฟล์เดียว (videoUrl/photoUrl) ด้วย
                            const vUrls = m.videoUrls?.length ? m.videoUrls : (m.videoUrl ? [m.videoUrl] : []);
                            const pUrls = m.photoUrls?.length ? m.photoUrls : (m.photoUrl ? [m.photoUrl] : []);
                            return (
                              <>
                                {vUrls.length > 0 && (
                                  <div>
                                    <div className="cp-ship-card-media-label">วีดีโอสินค้า{vUrls.length > 1 ? ` (${vUrls.length})` : ""}</div>
                                    {vUrls.map((url, i) => <video key={i} src={url} controls playsInline muted style={{ marginBottom: i < vUrls.length - 1 ? 6 : 0 }} />)}
                                  </div>
                                )}
                                {pUrls.length > 0 && (
                                  <div>
                                    <div className="cp-ship-card-media-label">รูปกล่องสินค้า{pUrls.length > 1 ? ` (${pUrls.length})` : ""}</div>
                                    <div className="cp-photo-grid">
                                      {pUrls.map((url, i) => (
                                        <a key={i} href={url} target="_blank" rel="noreferrer">
                                          <img src={url} alt={`กล่องสินค้า-${i}`} />
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <span className="cp-btime" style={{ textAlign: isRight ? "right" : "left" }}>
                        {m.createdAt?.toDate?.()?.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" }) || ""}
                      </span>
                    </div>
                  </div>
                );
              }

              // ── Delivery card (ผู้ขายส่งมอบไอดี/รหัสผ่าน) ──────────────────────
              if (m.type === "delivery") {
                const isRight = m.senderId === user.uid;
                return (
                  <div key={m.id} className={`cp-mrow${isRight ? " right" : ""}`}>
                    <div className="cp-av" style={{ background: "linear-gradient(135deg,#3a2a1a,#5a4a2a)", color: "#c77b27" }}><Icon name="key" size={18} /></div>
                    <div style={{ minWidth: 0, maxWidth: "100%" }}>
                      <div className="cp-bname">{m.senderName?.includes("@") ? m.senderName.split("@")[0] : m.senderName}</div>
                      <DeliveryCard data={m.delivery || { note: m.text }} />
                      <span className="cp-btime" style={{ textAlign: isRight ? "right" : "left" }}>
                        {m.createdAt?.toDate?.()?.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" }) || ""}
                      </span>
                    </div>
                  </div>
                );
              }

              // ── Image message ─────────────────────────────────────────
              if (m.type === "image") {
                const isRight = m.senderId === user.uid;
                const avBg = m.role === "buyer" ? "linear-gradient(135deg,#1a3a6a,#2a5a9a)" :
                              m.role === "admin" ? "linear-gradient(135deg,#3a2a0a,#5a4a1a)" :
                                                   "linear-gradient(135deg,#1a3a25,#2a5a35)";
                const char = <Icon name={m.role === "buyer" ? "user" : m.role === "admin" ? "shield" : "cart"} size={18} />;
                return (
                  <div key={m.id} className={`cp-mrow${isRight ? " right" : ""}`}>
                    <div className="cp-av" style={{ background: avBg }}>{char}</div>
                    <div>
                      <div className="cp-bname">{m.senderName?.includes("@") ? m.senderName.split("@")[0] : m.senderName}</div>
                      <a href={m.imageUrl} target="_blank" rel="noreferrer">
                        <img src={m.imageUrl} alt="รูปภาพ" className="cp-chat-img" />
                      </a>
                      <span className="cp-btime" style={{ textAlign: isRight ? "right" : "left" }}>
                        {m.createdAt?.toDate?.()?.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" }) || ""}
                      </span>
                    </div>
                  </div>
                );
              }

              const isRight = m.senderId === user.uid;
              const avBg =
                m.type === "buyer"  ? "linear-gradient(135deg,#1a3a6a,#2a5a9a)" :
                m.type === "ai"     ? "linear-gradient(135deg,#0a2a15,#1a5a30)" :
                m.type === "admin"  ? "linear-gradient(135deg,#3a2a0a,#5a4a1a)" :
                                      "linear-gradient(135deg,#1a3a25,#2a5a35)";
              const char = <Icon name={m.type === "buyer" ? "user" : m.type === "ai" ? "bot" : m.type === "admin" ? "shield" : "cart"} size={18} />;
              const bubbleCls =
                m.type === "buyer"  ? "buyer" :
                m.type === "ai"     ? "ai" :
                m.type === "admin"  ? "admin" :
                                      "seller";
              return (
                <div key={m.id} className={`cp-mrow${isRight ? " right" : ""}`}>
                  <div className="cp-av" style={{ background: avBg }}>{char}</div>
                  <div>
                    <div className="cp-bname" style={m.type === "admin" ? { color: "#c77b27" } : {}}>
                      {m.senderName?.includes("@") ? m.senderName.split("@")[0] : m.senderName}
                    </div>
                    <div className={`cp-bubble ${bubbleCls}`}>
                      {m.type === "ai" ? stripEmoji(m.text) : m.text}
                    </div>
                    <span className="cp-btime" style={{ textAlign: isRight ? "right" : "left" }}>
                      {m.createdAt?.toDate?.()?.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" }) || ""}
                    </span>
                  </div>
                </div>
              );
            })}
            {typing && (
              <div className="cp-mrow">
                <div className="cp-av" style={{ background: "linear-gradient(135deg,#0a2a15,#1a5a30)" }}><Icon name="bot" size={18} /></div>
                <div className="cp-bubble ai">
                  <div className="cp-typing"><div className="cp-td" /><div className="cp-td" /><div className="cp-td" /></div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile actions */}
          <div className="cp-mobile-actions">{renderEscrowActions()}</div>

          {room.status === "shipped" && <div className="kl-confirm-banner"><Icon name="clock" size={22} /><div><strong>{isBuyer ? "รอคุณตรวจสอบและยืนยันรับสินค้า" : "รอผู้ซื้อยืนยันรับสินค้า"}</strong><p>{isDigital ? "สินค้าดิจิทัลมีเวลายืนยัน 24 ชั่วโมงหลังส่งมอบ" : "กรุณาตรวจสอบสินค้าภายใน 7 วันหลังแจ้งจัดส่ง"} • หากมีปัญหาให้แจ้งในดีล</p></div></div>}

          {/* Input bar */}
          <div className="cp-inputbar">
            <div className="cp-quick">
              {["ได้รับข้อมูลแล้ว", "ขอบคุณครับ", "ขอรายละเอียดเพิ่มเติม"].map((t) => (
                <button key={t} className="cp-qbtn" onClick={() => setInput(t)}>{t}</button>
              ))}
            </div>
            <div className="cp-inputrow">
              {/* Hidden file input */}
              <input
                ref={chatImgInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) { setChatImgFile(file); e.target.value = ""; }
                }}
              />
              {/* Image button */}
              <button
                className="cp-img-btn"
                onClick={() => chatImgInputRef.current?.click()}
                title="ส่งรูปภาพ"
                disabled={chatImgLoading}
              ><Icon name="paperclip" size={21} /></button>
              <div className="cp-inputwrap" style={{ flexDirection: "column", alignItems: "stretch" }}>
                <textarea
                  className="cp-textarea"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                  aria-label="ข้อความถึงคู่ค้า"
                  placeholder="พิมพ์ข้อความถึงคู่ค้า..."
                  rows={1}
                />
                {/* Image preview bar */}
                {chatImgFile && (
                  <div className="cp-img-preview-bar">
                    <img className="cp-img-preview-thumb" src={URL.createObjectURL(chatImgFile)} alt="preview" />
                    <span className="cp-img-preview-name">{chatImgFile.name}</span>
                    <button className="cp-img-cancel" onClick={() => setChatImgFile(null)} title="ยกเลิก">✕</button>
                    <button className="cp-img-send" onClick={() => sendImage(chatImgFile)} disabled={chatImgLoading}>
                      {chatImgLoading ? "กำลังส่ง..." : "ส่งรูป"}
                    </button>
                  </div>
                )}
              </div>
              <button className="cp-send" onClick={send} disabled={sending || typing || !!chatImgFile || !input.trim() || (!isAdminMode && !room.members?.includes(user.uid))}>{sending ? "กำลังส่ง..." : "ส่งข้อความ"}</button>
            </div>
            <p className="kl-chat-safety">อย่าส่งรหัสผ่านธนาคารหรือรหัส OTP ในแชท</p>
          </div>
        </div>
      </div>

      {/* ── Mobile Panel Drawer ── */}
      {showPanelDrawer && (
        <>
          <div className="cp-panel-overlay open" onClick={() => setShowPanelDrawer(false)} />
          <div className="cp-panel-drawer" ref={panelDrawerRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="ข้อมูลดีล">
            <button className="kl-drawer-close" onClick={() => setShowPanelDrawer(false)}>ข้อมูลดีล <Icon name="x" size={20} /></button>
            <div className="cp-panel-handle" />
            <div style={{ padding: "16px 16px 24px" }}>
              <div className="cp-ptitle" style={{ marginBottom: 12, paddingTop: 4 }}>ข้อมูลดีล</div>
              {[
                ["สินค้า", room.itemName],
                ["ราคาสินค้า", `฿${(room.price || 0).toLocaleString()}`, "g"],
                ["ค่าธรรมเนียม", `฿${fee.toLocaleString()}`],
                ["รวมทั้งหมด", `฿${total.toLocaleString()}`, "g"],
                ["สถานะ", STATUS_LABEL[room.status] || room.status, "w"],
                ...(room.courierName ? [["บริษัทขนส่ง", room.courierName, "b"]] : []),
                ...(room.trackingNumber ? [["เลขพัสดุ", room.trackingNumber, "b"]] : []),
              ].map(([l, v, c]) => (
                <div key={l} className="cp-irow">
                  <span className="cp-rl">{l}</span>
                  <span className={`cp-rv${c ? " " + c : ""}`}>{v}</span>
                </div>
              ))}
              <div className="cp-ptitle" style={{ margin: "14px 0 10px" }}>ผู้เข้าร่วม</div>
              {room.buyerId ? (
                <div className="cp-member">
                  <div className="cp-mav" style={{ background: "linear-gradient(135deg,#1a3a6a,#2a5a9a)", color: "#607caa" }}><Icon name="cart" size={18} /></div>
                  <div>
                    <div className="cp-mname">{room.buyerName || "ผู้ซื้อ"}</div>
                    <div className="cp-mrole">ผู้ซื้อ{room.buyerId === user.uid && !isAdminMode ? " (คุณ)" : ""}</div>
                    {room.buyerId !== user.uid && partnerRating !== null && (
                      partnerRating.count > 0
                        ? <div className="cp-rating-badge"><Icon name="star-fill" size={12} /> {partnerRating.avg.toFixed(1)} <span style={{opacity:0.6}}>({partnerRating.count} รีวิว)</span></div>
                        : <div className="cp-rating-badge no-review">ยังไม่มีรีวิว</div>
                    )}
                  </div>
                </div>
              ) : <div className="cp-waiting">รอผู้ซื้อเข้าร่วม</div>}
              {room.sellerId ? (
                <div className="cp-member">
                  <div className="cp-mav" style={{ background: "linear-gradient(135deg,#1a3a25,#2a5a35)", color: "#dc3728" }}><Icon name="briefcase" size={18} /></div>
                  <div>
                    <div className="cp-mname">{room.sellerName || "ผู้ขาย"}</div>
                    <div className="cp-mrole">ผู้ขาย{room.sellerId === user.uid && !isAdminMode ? " (คุณ)" : ""}</div>
                    {room.sellerId !== user.uid && partnerRating !== null && (
                      partnerRating.count > 0
                        ? <div className="cp-rating-badge"><Icon name="star-fill" size={12} /> {partnerRating.avg.toFixed(1)} <span style={{opacity:0.6}}>({partnerRating.count} รีวิว)</span></div>
                        : <div className="cp-rating-badge no-review">ยังไม่มีรีวิว</div>
                    )}
                  </div>
                </div>
              ) : <div className="cp-waiting">รอผู้ขายเข้าร่วม</div>}
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODALS
      ════════════════════════════════════════════════════════════ */}

      {/* ── Terms & Role modal ── */}
      {showTerms && (
        <div className="cp-modal">
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">ข้อตกลงการใช้งาน Klang</div>
            <div className="cp-terms-box">
              <h4>ข้อกำหนดและเงื่อนไขการใช้บริการ</h4>
              <p><strong>1. ระบบ Escrow:</strong> Klang ทำหน้าที่เป็นตัวกลางถือเงิน ผู้ขายจะได้รับเงินก็ต่อเมื่อผู้ซื้อยืนยันรับสินค้าแล้ว หรือครบ 7 วันนับจากวันที่จัดส่ง</p>
              <p><strong>2. ค่าธรรมเนียม:</strong> ราคาต่ำกว่า ฿1,000 คิด ฿50 คงที่ / ฿1,000 ขึ้นไปคิด ฿100 คงที่ ชำระโดยผู้ซื้อ (ไม่คืนเงินค่าธรรมเนียมในทุกกรณี)</p>
              <p><strong>3. การจัดส่ง:</strong> ผู้ขายต้องจัดส่งสินค้าภายใน 3 วันนับจากที่ได้รับเงิน พร้อมแนบหลักฐานการส่ง</p>
              <p><strong>4. การรับสินค้า:</strong> ผู้ซื้อต้องตรวจสอบสินค้าและยืนยันรับภายใน 7 วัน มิฉะนั้นระบบจะโอนเงินให้ผู้ขายอัตโนมัติ</p>
              <p><strong>5. ข้อพิพาท:</strong> หากมีปัญหา ผู้ซื้อสามารถเรียกร้องได้ภายใน 7 วัน ทีม Klang จะตรวจสอบและตัดสินภายใน 24 ชั่วโมง</p>
              <p><strong>6. ความรับผิดชอบ:</strong> Klang ไม่รับผิดชอบความเสียหายที่เกิดจากการกระทำของผู้ใช้งานที่ฝ่าฝืนข้อตกลง</p>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 8 }}>คุณเข้าร่วมในฐานะ:</div>
              <div className="cp-role-btns">
                <button className={`cp-role-btn${joinRole === "buyer" ? " active" : ""}`}
                  onClick={() => setJoinRole("buyer")} disabled={!!room.buyerId && room.buyerId !== user.uid}>
                  ผู้ซื้อ
                </button>
                <button className={`cp-role-btn${joinRole === "seller" ? " active" : ""}`}
                  onClick={() => setJoinRole("seller")} disabled={!!room.sellerId && room.sellerId !== user.uid}>
                  ผู้ขาย
                </button>
              </div>
            </div>

            <label className="cp-terms-check">
              <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
              ฉันได้อ่านและยอมรับข้อกำหนดและเงื่อนไขการใช้งาน Klang
            </label>

            {modalErr && <div className="cp-merr">{modalErr}</div>}

            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => navigate("/home")}>ยกเลิก</button>
              <button className="cp-mok" onClick={handleAcceptTerms} disabled={!termsAccepted}>
                ยืนยันและเข้าห้องแชท →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pay modal ── */}
      {modal === "pay" && (
        <div className="cp-modal" onClick={(e) => {
          if (e.target === e.currentTarget) { setModal(null); clearCoupon(); }
        }}>
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">ยืนยันการชำระเงิน</div>

            {/* Wallet balance */}
            <div className={`cp-balance-box${walletBalance < discountedTotal ? " cp-balance-low" : ""}`}>
              <div>
                <div className="cp-balance-label">ยอดเงินในกระเป๋า</div>
                <div className="cp-balance-val">฿{walletBalance.toLocaleString()}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="cp-balance-label">ต้องการ</div>
                <div className="cp-balance-val">฿{discountedTotal.toLocaleString()}</div>
              </div>
            </div>

            {/* Insufficient balance warning */}
            {walletBalance < discountedTotal && (
              <div className="cp-merr" style={{ marginBottom: 12 }}>
                ยอดเงินไม่เพียงพอ ขาดอีก ฿{(discountedTotal - walletBalance).toLocaleString()}
                <div style={{ marginTop: 6 }}>
                  <button className="cp-topup-link" onClick={() => navigate("/wallet")}>เติมเงินที่นี่</button>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="cp-msummary">
              <div className="cp-msrow">
                <span>ราคาสินค้า</span>
                <span>฿{(room.price || 0).toLocaleString()}</span>
              </div>
              <div className="cp-msrow">
                <span>ค่าธรรมเนียม Klang</span>
                <span>
                  {couponDiscount > 0 && (
                    <span className="cp-orig-fee">฿{originalFee.toLocaleString()}</span>
                  )}
                  ฿{finalFee.toLocaleString()}
                </span>
              </div>
              {couponDiscount > 0 && (
                <div className="cp-msrow">
                  <span className="cp-discount-row">ส่วนลดคูปอง ({appliedCoupon.code})</span>
                  <span className="cp-discount-row">-฿{couponDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="cp-msrow">
                <span>รวมทั้งหมด</span>
                <span>฿{discountedTotal.toLocaleString()}</span>
              </div>
            </div>

            {/* ── Coupon input ── */}
            <div className="cp-coupon-wrap">
              <div className="cp-coupon-label">รหัสคูปองส่วนลดค่าธรรมเนียม</div>
              {appliedCoupon ? (
                <div className="cp-coupon-ok">
                  <span className="cp-coupon-ok-left">
                    <strong>{appliedCoupon.code}</strong>
                    &nbsp;—&nbsp;
                    {appliedCoupon.type === "percent"
                      ? `ลด ${appliedCoupon.value}%`
                      : `ลด ฿${appliedCoupon.value}`}
                    &nbsp;= ประหยัด ฿{couponDiscount.toLocaleString()}
                  </span>
                  <button className="cp-coupon-ok-right" onClick={clearCoupon}>✕ ยกเลิก</button>
                </div>
              ) : (
                <>
                  <div className="cp-coupon-row">
                    <input
                      className="cp-coupon-input"
                      placeholder="กรอกรหัสคูปอง"
                      value={couponInput}
                      onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponErr(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                      maxLength={20}
                    />
                    <button
                      className="cp-coupon-btn"
                      onClick={handleApplyCoupon}
                      disabled={!couponInput.trim() || couponLoading}
                    >
                      {couponLoading ? "..." : "ใช้คูปอง"}
                    </button>
                  </div>
                  {couponErr && <div className="cp-coupon-err">{couponErr}</div>}
                </>
              )}
            </div>

            {/* Fee info */}
            <div className="cp-fee-tiers">
              <div className="cp-fee-tiers-title">ค่าธรรมเนียม Escrow</div>
              <div className={`cp-fee-row${(room.price || 0) < 1000 ? " active-tier" : ""}`}>
                <span>ต่ำกว่า ฿1,000</span>
                <span>฿50 คงที่{(room.price || 0) < 1000 ? " ✓" : ""}</span>
              </div>
              <div className={`cp-fee-row${(room.price || 0) >= 1000 ? " active-tier" : ""}`}>
                <span>฿1,000 ขึ้นไป</span>
                <span>฿100 คงที่{(room.price || 0) >= 1000 ? " ✓" : ""}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="cp-fee-row active-tier" style={{borderTop:"1px solid rgba(247,71,53,0.2)",marginTop:4,paddingTop:6}}>
                  <span>หลังใช้คูปอง</span>
                  <span>฿{finalFee.toLocaleString()} (ลด ฿{couponDiscount.toLocaleString()})</span>
                </div>
              )}
            </div>

            <div className="cp-mdesc" style={{ marginBottom: 16 }}>
              เงินจะถูกล็อกไว้ในกองกลาง จะโอนให้ผู้ขายก็ต่อเมื่อคุณกดยืนยันรับสินค้า หรือครบ 7 วัน
            </div>
            {modalErr && <div className="cp-merr">{modalErr}</div>}
            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => { setModal(null); clearCoupon(); }}>ยกเลิก</button>
              <button className="cp-mok pay" onClick={handlePay} disabled={actionLoading || walletBalance < discountedTotal}>
                {actionLoading ? "กำลังชำระ..." : `ยืนยันชำระ ฿${discountedTotal.toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ship modal ── */}
      {modal === "ship" && (
        <div className="cp-modal" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">แจ้งจัดส่งสินค้า</div>

            {/* 3-day deadline warning */}
            <div className="cp-deadline-banner">
              <span>กรุณาจัดส่งสินค้าภายใน <strong>3 วัน</strong> นับจากผู้ซื้อชำระเงิน</span>
            </div>

            {/* Required checklist status */}
            <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 12px",marginBottom:14,fontSize:"0.78rem"}}>
              <div style={{color:"var(--text-muted)",marginBottom:7,fontSize:"0.65rem",letterSpacing:"1px",textTransform:"uppercase"}}>ต้องกรอกให้ครบก่อนกดยืนยัน</div>
              {[
                ["เลือกบริษัทขนส่ง", !!courier],
                ["กรอกเลขพัสดุ", !!tracking.trim()],
                ["แนบวีดีโอสินค้า", videoFiles.length > 0],
                ["แนบรูปกล่องสินค้า", photoFiles.length > 0],
              ].map(([label, done]) => (
                <div key={label} style={{display:"flex",alignItems:"center",gap:8,padding:"3px 0",color:done?"var(--accent)":"var(--text-muted)"}}>
                  <span style={{fontSize:"0.85rem"}}>{done ? <Icon name="check" size={14} /> : ""}</span>
                  <span style={{textDecoration:done?"line-through":"none",opacity:done?0.7:1}}>{label}</span>
                </div>
              ))}
            </div>

            {modalErr && <div className="cp-merr">{modalErr}</div>}

            {/* Courier dropdown */}
            <select className="cp-mselect" value={courier} onChange={(e) => setCourier(e.target.value)}>
              <option value="">— เลือกบริษัทขนส่ง —</option>
              <option value="Kerry Express">Kerry Express</option>
              <option value="J&T Express">J&T Express</option>
              <option value="Flash Express">Flash Express</option>
              <option value="Thailand Post">ไปรษณีย์ไทย (EMS)</option>
              <option value="DHL">DHL</option>
              <option value="Ninja Van">Ninja Van</option>
              <option value="Best Express">Best Express</option>
              <option value="อื่นๆ">อื่นๆ</option>
            </select>

            <input className="cp-minput" placeholder="เลขพัสดุ เช่น TH1234567890" value={tracking} onChange={(e) => setTracking(e.target.value)} />

            {/* Preview summary */}
            {(courier || tracking) && (
              <div className="cp-ship-preview">
                <div className="cp-ship-preview-title">ตัวอย่างข้อมูลที่จะส่ง</div>
                {courier && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", marginBottom: 5 }}>
                    <span style={{ color: "var(--text-muted)" }}>บริษัทขนส่ง</span>
                    <span style={{ color: "var(--warning)", fontWeight: 600 }}>{courier}</span>
                  </div>
                )}
                {tracking && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>เลขพัสดุ</span>
                    <span style={{ color: "var(--warning)", fontWeight: 600, fontFamily: "DM Mono,monospace" }}>{tracking}</span>
                  </div>
                )}
              </div>
            )}

            {renderShipPhotos()}

            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => setModal(null)}>ยกเลิก</button>
              <button className="cp-mok" style={{ background: "var(--warning)", color: "#f5f6f8" }}
                onClick={handleShip} disabled={actionLoading || !tracking.trim() || !courier || videoFiles.length === 0 || photoFiles.length === 0}>
                {actionLoading
                  ? uploadingPhotos ? "กำลังอัปโหลด..." : "กำลังบันทึก..."
                  : "✓ ยืนยันจัดส่งสินค้า"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Deliver modal (สินค้าดิจิทัล) ── */}
      {modal === "deliver" && (
        <div className="cp-modal" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">ส่งมอบโค้ด/บัญชี</div>

            <div className="cp-deadline-banner">
              <span>ผู้ซื้อมีเวลายืนยัน <strong>24 ชั่วโมง</strong> หลังส่งมอบ — เตรียมรหัส/ข้อมูลให้ถูกต้องก่อนส่ง</span>
            </div>

            {modalErr && <div className="cp-merr">{modalErr}</div>}

            <div className="cp-dfield">
              <label className="cp-dlabel"><Icon name="user" size={14} /> ไอดี / อีเมล / ชื่อผู้ใช้</label>
              <input
                className="cp-dinput"
                placeholder="เช่น user@example.com หรือ ชื่อผู้ใช้ในเกม"
                value={deliveryId}
                onChange={(e) => setDeliveryId(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <div className="cp-dfield">
              <label className="cp-dlabel"><Icon name="lock" size={14} /> รหัสผ่าน</label>
              <div className="cp-dwrap">
                <input
                  className="cp-dinput has-btn"
                  type={showDeliveryPw ? "text" : "password"}
                  placeholder="รหัสผ่านของบัญชี"
                  value={deliveryPw}
                  onChange={(e) => setDeliveryPw(e.target.value)}
                  autoComplete="new-password"
                  spellCheck={false}
                />
                <button type="button" className="cp-deye" onClick={() => setShowDeliveryPw((v) => !v)} title={showDeliveryPw ? "ซ่อน" : "แสดง"}>
                  <Icon name={showDeliveryPw ? "eye-off" : "eye"} size={18} />
                </button>
              </div>
            </div>

            <div className="cp-dfield">
              <label className="cp-dlabel"><Icon name="info" size={14} /> อื่นๆ <span style={{ opacity: 0.6, fontWeight: 400 }}>(ไม่บังคับ)</span></label>
              <textarea
                className="cp-dinput cp-dtext"
                placeholder={"เช่น อีเมลกู้คืน, รหัสยืนยัน 2 ขั้นตอน, รหัสเติมเกม, วิธีเข้าใช้ หรือข้อควรระวัง"}
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                rows={3}
              />
            </div>

            <div className="cp-dhint">
              <Icon name="shield" size={14} />
              <span>ข้อมูลนี้จะถูกส่งเข้าแชทเป็นหลักฐาน — ผู้ซื้อและแอดมิน (กรณีมีข้อพิพาท) จะเห็นข้อมูลนี้ กรอกอย่างน้อย 1 ช่อง</span>
            </div>

            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => setModal(null)}>ยกเลิก</button>
              <button className="cp-mok" style={{ background: "var(--warning)", color: "#f5f6f8" }}
                onClick={handleDeliver} disabled={actionLoading || (!deliveryId.trim() && !deliveryPw.trim() && !deliveryNote.trim())}>
                {actionLoading ? "กำลังส่ง..." : "ยืนยันส่งมอบ"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Receive modal ── */}
      {modal === "receive" && (
        <div className="cp-modal" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">{isDigital ? "ยืนยันว่าใช้งานได้จริง" : "ยืนยันรับสินค้า"}</div>

            {/* Tracking info card */}
            {(room.trackingNumber || room.courierName) && (
              <div className="cp-tracking-card">
                <div style={{ fontSize: "0.65rem", letterSpacing: "1px", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>ข้อมูลการจัดส่ง</div>
                {room.courierName && (
                  <div className="cp-tracking-row">
                    <span style={{ color: "var(--text-muted)" }}>บริษัทขนส่ง</span>
                    <span>{room.courierName}</span>
                  </div>
                )}
                {room.trackingNumber && (
                  <div className="cp-tracking-row">
                    <span style={{ color: "var(--text-muted)" }}>เลขพัสดุ</span>
                    <span style={{ fontFamily: "DM Mono,monospace" }}>{room.trackingNumber}</span>
                  </div>
                )}
              </div>
            )}

            {/* Seller proof: video(s) + box photo(s) */}
            {(roomVideoUrls.length > 0 || roomPhotoUrls.length > 0) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: "0.65rem", letterSpacing: "1px", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>หลักฐานจากผู้ขาย</div>
                {roomVideoUrls.length > 0 && (
                  <div style={{ marginBottom: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>วีดีโอสินค้า ({roomVideoUrls.length})</div>
                    {roomVideoUrls.map((url, i) => (
                      <video
                        key={i}
                        src={url}
                        controls
                        playsInline
                        style={{ width: "100%", borderRadius: 8, border: "1px solid var(--border)", background: "#000", maxHeight: 180 }}
                      />
                    ))}
                  </div>
                )}
                {roomPhotoUrls.length > 0 && (
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginBottom: 4 }}>รูปกล่องสินค้า ({roomPhotoUrls.length})</div>
                    <div className="cp-photo-grid">
                      {roomPhotoUrls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer">
                          <img
                            src={url} alt={`box-${i}`}
                            style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)", display: "block" }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Interactive checklist */}
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 8 }}>กรุณาตรวจสอบและติ๊กให้ครบก่อนยืนยัน:</div>
            <div className="cp-checklist">
              {receiveChecklist.map((label, i) => (
                <div key={i} className="cp-check-item" onClick={() => {
                  const next = [...checkItems]; next[i] = !next[i]; setCheckItems(next);
                }}>
                  <div className={`cp-check-box${checkItems[i] ? " checked" : ""}`}>
                    {checkItems[i] ? "✓" : ""}
                  </div>
                  <span style={{ color: checkItems[i] ? "var(--text)" : "var(--text-muted)" }}>{label}</span>
                </div>
              ))}
            </div>

            <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
              เมื่อยืนยันแล้ว Klang จะโอนเงิน{" "}
              <strong style={{ color: "var(--accent)" }}>฿{(room.price || 0).toLocaleString()}</strong>{" "}
              ให้ผู้ขายทันที — ไม่สามารถยกเลิกได้
            </div>

            {modalErr && <div className="cp-merr">{modalErr}</div>}
            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => { setModal(null); setCheckItems(new Array(receiveChecklist.length).fill(false)); }}>ยกเลิก</button>
              <button className="cp-mok receive" onClick={handleConfirmReceive}
                disabled={actionLoading || checkItems.length !== receiveChecklist.length || !checkItems.every(Boolean)}>
                {actionLoading ? "กำลังโอนเงิน..." : isDigital ? "ยืนยันว่าใช้งานได้จริง → โอนเงินให้ผู้ขาย" : "ยืนยันรับของ → โอนเงินให้ผู้ขาย"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dispute modal ── */}
      {modal === "dispute" && (
        <div className="cp-modal" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">เรียกร้องความเสียหาย</div>
            <div className="cp-mdesc">Klang จะระงับการโอนเงินชั่วคราว Admin จะตรวจสอบภายใน 24 ชั่วโมง</div>
            {modalErr && <div className="cp-merr">{modalErr}</div>}
            <textarea className="cp-mta" rows={4} value={dispute}
              onChange={(e) => setDispute(e.target.value)}
              placeholder="อธิบายปัญหาที่พบ เช่น: สินค้าเสียหาย, ไม่ตรงตามที่โฆษณา, ไม่ได้รับสินค้า..." />
            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => setModal(null)}>ยกเลิก</button>
              <button className="cp-mdanger" onClick={handleDispute} disabled={actionLoading}>
                {actionLoading ? "กำลังส่ง..." : "ส่งเรื่องร้องเรียน"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review modal ── */}
      {modal === "review" && (
        <div className="cp-modal" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="cp-mbox" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={showTerms ? "ข้อตกลงการใช้งาน" : "ดำเนินการในดีล"}>
            <div className="cp-mtitle">ให้คะแนนคู่ค้า</div>
            <div className="cp-mdesc">
              รีวิว: <strong>{isBuyer ? (room.sellerName || "ผู้ขาย") : (room.buyerName || "ผู้ซื้อ")}</strong>
              <br/>สินค้า: {room.itemName}
            </div>

            {/* Star selector */}
            <div className="cp-star-row">
              {[1,2,3,4,5].map((i) => (
                <span
                  key={i}
                  className={`cp-star${(reviewHover || reviewStars) >= i ? " filled" : ""}`}
                  onClick={() => setReviewStars(i)}
                  onMouseEnter={() => setReviewHover(i)}
                  onMouseLeave={() => setReviewHover(0)}
                >★</span>
              ))}
            </div>
            <div style={{textAlign:'center',fontSize:'0.88rem',fontWeight:700,color:'#ffcc00',marginBottom:14}}>
              {reviewStars} / 5 ดาว — {["","แย่มาก","แย่","พอใช้","ดี","ดีมาก"][reviewStars]}
            </div>

            <textarea
              className="cp-mta"
              rows={3}
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="แสดงความคิดเห็น... (ไม่บังคับ)"
            />

            <div className="cp-mbtns">
              <button className="cp-mcancel" onClick={() => setModal(null)}>ยกเลิก</button>
              <button
                className="cp-mok"
                style={{background:'#ffcc00',color:'#f5f6f8'}}
                onClick={handleReview}
                disabled={reviewSubmitting || !reviewStars}>
                {reviewSubmitting ? "กำลังส่ง..." : "ส่งรีวิว"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
